<template>
    <div>
        <div v-if="isPageLoading" class="page-loading-overlay">
            <div class="loading-spinner-container">
                <div class="loading-spinner"></div>
                <div class="loading-text">Loading Invoice...</div>
                <div class="loading-progress-bar">
                    <div class="loading-progress-fill" :style="{ width: loadingProgress + '%' }"></div>
                </div>
            </div>
        </div>
        
        <!-- Screen Header (hidden when printing) -->
        <div class="screen-only">
            <!-- Child Invoices Alert -->
            <div v-if="invoice.child_invoices && invoice.child_invoices.length > 0" 
                 style="margin: 10px auto; max-width: 90%;">
                <b-alert 
                    :show="true" 
                    variant="warning" 
                    class="child-invoice-alert"
                    style="border-left: 4px solid #ff9800; margin-bottom: 0;"
                >
                    <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
                        <div style="display: flex; align-items: center; gap: 10px;">
                            <span style="font-size: 24px;">🔄</span>
                            <div>
                                <strong style="font-size: 16px;">EXCHANGE INVOICE CREATED</strong>
                                <div style="font-size: 12px; color: #666;">
                                    This order has been split or modified. A new invoice has been created.
                                </div>
                            </div>
                        </div>
                        <div style="display: flex; gap: 8px; align-items: center;">
                            <b-badge variant="warning" style="font-size: 14px; padding: 8px 16px;">
                                📄 {{ invoice.child_invoices.length }} EXCHANGE INVOICE{{ invoice.child_invoices.length > 1 ? 's' : '' }}
                            </b-badge>
                            <b-button 
                                size="md" 
                                variant="warning" 
                                @click.stop="viewChildInvoice(invoice.child_invoices[0])"
                                style="font-weight: bold;"
                            >
                                VIEW EXCHANGE INVOICE: {{ invoice.child_invoices[0] }}
                            </b-button>
                        </div>
                    </div>
                </b-alert>
                
                <!-- Show all child invoices if multiple -->
                <div v-if="invoice.child_invoices.length > 1" style="margin-top: 8px; text-align: right;">
                    <span style="font-size: 12px; color: #666;">Other child invoices: </span>
                    <b-link 
                        v-for="(childId, idx) in invoice.child_invoices.slice(1)" 
                        :key="childId"
                        href="#"
                        @click.prevent="viewChildInvoice(childId)"
                        style="margin-left: 8px;"
                    >
                        {{ childId }}
                    </b-link>
                </div>
            </div>

            <!-- Enhanced Exchange Invoice Banner -->
            <div v-if="invoice.invoice_type === 'EX'" 
                 style="margin: 10px auto; max-width: 90%;">
                <b-alert 
                    :show="true" 
                    variant="primary" 
                    class="exchange-invoice-alert"
                    style="border-left: 4px solid #007bff; margin-bottom: 0; background: #f0f7ff;"
                >
                    <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
                        <div style="display: flex; align-items: center; gap: 10px;">
                            <span style="font-size: 28px;">🔄</span>
                            <div>
                                <strong style="font-size: 18px; color: #0056b3;">EXCHANGE INVOICE</strong>
                                <div style="font-size: 13px; color: #444;">
                                    Parent Invoice: <strong>{{ invoice.parent_invoice_id }}</strong>
                                    <span v-if="parentInvoiceData && parentInvoiceData.customer" style="margin-left: 10px; color: #666;">
                                        ({{ parentInvoiceData.customer.fullname || 'Customer' }})
                                    </span>
                                </div>
                                <div v-if="invoice.child_invoices && invoice.child_invoices.length > 0" 
                                     style="font-size: 12px; color: #666; margin-top: 4px;">
                                    <span>Child Invoices: </span>
                                    <b-link 
                                        v-for="(childId, idx) in invoice.child_invoices" 
                                        :key="childId"
                                        href="#"
                                        @click.prevent="viewChildInvoice(childId)"
                                        style="margin-right: 8px; font-weight: bold;"
                                    >
                                        {{ childId }}
                                    </b-link>
                                </div>
                            </div>
                        </div>
                        <div style="display: flex; gap: 8px; align-items: center;">
                            <b-badge variant="primary" style="font-size: 14px; padding: 8px 16px;">
                                📄 EXCHANGE
                            </b-badge>
                            <b-button 
                                size="md" 
                                variant="primary" 
                                @click="viewParentInvoice"
                                style="font-weight: bold;"
                            >
                                VIEW PARENT: {{ invoice.parent_invoice_id }}
                            </b-button>
                        </div>
                    </div>
                </b-alert>
            </div>

            <!-- Parent Payment Summary for Exchange Invoices -->
            <div v-if="invoice.invoice_type === 'EX' && parentInvoiceData" 
                 style="margin: 10px auto; max-width: 90%;">
                <b-card style="background: #f8f9fa; border: 1px solid #dee2e6;">
                    <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
                        <div>
                            <strong>Parent Invoice Total:</strong> 
                            <span style="color: #0056b3;">${{ parentInvoiceTotal.toFixed(2) }}</span>
                        </div>
                        <div>
                            <strong>Exchange Items Total:</strong> 
                            <span style="color: #28a745;">${{ getExchangeTotal.toFixed(2) }}</span>
                        </div>
                        <div v-if="parentPaymentTotal > 0">
                            <strong>Parent Payment Allocated:</strong> 
                            <span style="color: #0056b3;">${{ allocatedParentPaymentTotal.toFixed(2) }}</span>
                        </div>
                        <div v-if="getExchangeTotal < parentPaymentTotal && parentPaymentTotal > 0" 
                             style="color: #856404;">
                            <strong>Remaining Store Credit:</strong> 
                            <span>${{ (parentPaymentTotal - getExchangeTotal).toFixed(2) }}</span>
                        </div>
                        <div v-if="getExchangeTotal > parentPaymentTotal && parentPaymentTotal > 0" 
                             style="color: #dc3545;">
                            <strong>Additional Amount Due:</strong> 
                            <span>${{ (getExchangeTotal - parentPaymentTotal).toFixed(2) }}</span>
                        </div>
                    </div>
                    <!-- Parent Payment Methods Detail -->
                    <div v-if="getParentPaymentMethodsForItems.length > 0" 
                         style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #dee2e6;">
                        <div style="font-size: 12px; color: #666;">
                            <strong>Parent Payment Details:</strong>
                        </div>
                        <div style="display: flex; flex-wrap: wrap; gap: 15px; margin-top: 5px;">
                            <div v-for="pm in getParentPaymentMethodsForItems" :key="pm.id || pm.name" 
                                 style="font-size: 12px;">
                                <span style="font-weight: bold;">{{ (pm.value || '').toUpperCase() }} - {{ pm.name }}:</span>
                                <span style="color: #0056b3;">${{ pm.display_amount }}</span>
                            </div>
                        </div>
                    </div>
                </b-card>
            </div>

            <TicketListModal 
                v-if="currentTicketItem"
                :item="currentTicketItem"
                :tickets="currentTickets"
                @view-ticket="handleViewTicket"
                @create-ticket="handleCreateTicket"
                @closed="currentTicketItem = null"
            />
            
            <b-container class="bv-example-row" style="margin-top: 50px; max-width: 80% !important;">
                <b-row>
                    <b-col class="hidden-print">
                        <div v-if="invoice_location_details.short_name && invoice_location_details.short_name.split('SEN').length > 1">
                            <div><h2><b>INT FOURNITURE</b></h2></div>
                            <div>
                                <div><sub><b>{{invoice_location_details.address}}</b></sub></div>
                                <div><sub>{{invoice_location_details.email}}</sub></div>
                                <div><sub>{{invoice_location_details.phone}}</sub></div>
                            </div>
                        </div>
                        <div v-else class="ml-2 ml-md-0">
                            <div><h2><b>Furniture Mecca</b></h2></div>
                            <div>
                                <div><sub><b>{{invoice_location_details.address}}</b></sub></div>
                                <div><sub>{{invoice_location_details.email}}</sub></div>
                                <div><sub>{{invoice_location_details.phone}}</sub></div>
                                <div><sub>{{invoice_location_details.website}}</sub></div>
                            </div>
                        </div>
                    </b-col>
                    <b-col class="hidden-print">
                        <div>
                            <div>
                                <div><b>INVOICE NO {{ invoice.invoice_id }}</b></div>
                            </div>
                            <div v-if="invoice.is_layaway">
                                <div style="color: red;"><b>LAYAWAY SALE</b></div>
                            </div>
                            <div v-if="invoice.invoice_type === 'EX'">
                                <div style="color: #0056b3;"><b>EXCHANGE INVOICE</b></div>
                            </div>
                            <div>
                                <div>SALE DATE: {{invoice.sale_date}}</div>
                            </div>
                            <div>
                                <div>SALES AGENT: {{invoice.username ? invoice.username.toUpperCase() : ''}}</div>
                            </div>
                            <div>
                                <div>LOC: {{packet.locationsdict ? packet.locationsdict[invoice.location_id] : ''}}</div>
                            </div>

                            <div v-if="invoice.available_credit_value > 0">
                                <div style="max-width: 200px;"><b-alert show>STORE CREDIT: ${{invoice.available_credit_value}}</b-alert></div>
                            </div>
                        </div>

                        <div style="margin-top: 10px;">
                            <b-card-text class="d-flex flex-wrap" style="gap: 5px;">
                                <b-button size="sm" variant="outline-dark" class="hidden-print" @click="print_it" style="width: 120px;">PRINT INVOICE</b-button>
                                <b-button size="sm" variant="outline-success" class="hidden-print" :disabled="update_disabled" @click="reload_invoice()">RELOAD</b-button>
                                <b-button size="sm" variant="outline-info" class="hidden-print" @click="printReceipt" style="">
                                    🖨️ REGISTER RECEIPT
                                </b-button>
                                <b-button size="sm" variant="outline-danger" v-if="check_if_edit_allowed()" class="hidden-print" :disabled="update_disabled" @click="edit_invoice()">
                                    <span v-if="edit_invoice_action">CANCEL EDIT</span>
                                    <span v-else>EDIT</span>
                                </b-button>
                            </b-card-text>
                        </div>
                    </b-col>
                    <b-col class="hidden-print" md="3" style="display:flex; justify-content: center; align-items: center;">
                        <b-card-text style="margin-bottom:0px !important">
                             <span>TRACK OR RESCHEDULE YOUR ORDER</span>
                        </b-card-text>
                         <b-card-text>
                            <img src="@/assets/QRCODE.png" width="140px" alt="QR Code"></img>
                        </b-card-text>

                        <b-card-text class="ml-2">
                            <span v-if="[1, 3].includes(invoice.refund_request_approved)">
                                <b-button size="sm" variant="outline-danger" :disabled="true" class="hidden-print">EDIT APPROVAL PENDING</b-button>
                            </span>
                            <span v-else-if="invoice.refund_request_approved == 2">
                                <b-button size="sm" variant="outline-primary" class="hidden-print" @click="complete_refund(invoice.invoice_id)">COMPLETE EDIT</b-button>
                            </span>
                            <span v-else>
                                <b-button size="sm" variant="outline-secondary" class="hidden-print" @click="request_refund(invoice.invoice_id)">REQUEST EDIT</b-button>
                            </span>
                        </b-card-text>
                    </b-col>
                </b-row>
            </b-container>
        </div>

        <!-- PRINT HEADER -->
        <table class="print-header-container">
            <thead>
                <tr>
                    <td>
                        <div class="print-header">
                            <div style="width: 100%; max-width: 100%; margin: 0 auto; padding: 10px 0; font-family: Arial, sans-serif;">
                                <div style="display: flex; width: 100%; justify-content: space-between; align-items: flex-start; gap: 10px;">
                                    
                                    <!-- Left Column: Invoice Details -->
                                    <div style="flex: 1; max-width: 20%; padding: 5px;">
                                        <div v-if="invoice.is_layaway" style="color: red; font-weight: bold; font-size: 14px; white-space: nowrap;">LAYAWAY SALE</div>
                                        <div v-else-if="invoice.invoice_type === 'EX'" style="text-align: left; color: #0056b3; font-weight: bold; font-size: 14px; white-space: nowrap;">EXCHANGE SALE</div>
                                        <div v-else style="text-align: left; color: blue; font-weight: bold; font-size: 14px; white-space: nowrap;">SALE</div>
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">{{invoice.sale_date}}</div>    
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">{{ invoice.invoice_id }}</div>
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">{{ invoice.username ? invoice.username.toUpperCase() : '' }}</div>
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">{{invoice_location_details.phone}}</div>
                                        <div v-if="financeLeaseInfo.lease_id" style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px; background-color: #f0f0f0;">
                                            <strong>Lease ID:</strong> {{ financeLeaseInfo.lease_id }}
                                        </div>
                                        <div v-if="financeLeaseInfo.misc_info" style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px; background-color: #f0f0f0;">
                                            <strong>Misc:</strong> {{ financeLeaseInfo.misc_info }}
                                        </div>
                                        <div v-if="invoice.invoice_type === 'EX' && invoice.parent_invoice_id" style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px; background-color: #e8f0fe; color: #0056b3;">
                                            <strong>Parent Invoice:</strong> {{ invoice.parent_invoice_id }}
                                        </div>
                                    </div>
                                    
                                    <!-- Center Column: Store Info -->
                                    <div style="flex: 1; max-width: 35%; padding: 5px; text-align: center;">
                                        <div style="font-size: 28px; color:#d32f2f; font-weight: bold; white-space: nowrap;">FURNITURE MECCA</div>
                                        <div style="font-size: 14px; white-space: nowrap;">www.myfurnituremecca.com</div>
                                        <div style="font-size: 12px; white-space: nowrap;">215 352 1600</div>
                                        <div style="font-size: 12px;">{{invoice_location_details.address}}</div>
                                    </div>
                                    
                                    <!-- Right Column: Customer Information -->
                                    <div style="flex: 1; max-width: 25%; padding: 5px; justify-content: flex-end;">
                                        <div style="color:blue; font-weight: bold; white-space: nowrap;">CUSTOMER INFORMATION</div>
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">
                                            NAME: {{ getCustomerFullName() }}
                                        </div>
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">
                                            ADDRESS: {{ getCustomerAddress() }}
                                        </div>
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">
                                            {{ getCustomerCityStateZip() }}
                                        </div>
                                        <div style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">
                                            PHONE 1: {{ getCustomerPhone() }}
                                        </div>
                                        <div v-if="getCustomerPhone2()" style="font-size: 12px; border: 0.5px solid lightgray; padding: 2px 4px; margin-top: 2px;">
                                            PHONE 2: {{ getCustomerPhone2() }}
                                        </div>
                                    </div>
                                    
                                    <!-- QR Code Column for Print Version -->
                                    <div class="print-only" style="flex: 1; max-width: 10%; padding: 5px; text-align: center;">
                                        <div style="font-size: 10px; font-weight: bold; margin-bottom: 5px;">TRACK YOUR ORDER</div>
                                        <img src="@/assets/QRCODE.png" width="100px" alt="QR Code"style="border: 1px solid #ddd; padding: 5px;"></img>
                                        <div style="font-size: 8px; margin-top: 5px;">Scan to track or reschedule</div>
                                    </div>
                                </div>
                                <div v-if="invoice.comment_txt" style="margin-top: 15px; padding: 8px; background-color: #fff9e6; border: 1px solid #ffc107; border-radius: 4px;">
                                    <div style="display: flex; gap: 10px;">
                                        <div style="font-weight: bold; color: #856404; min-width: 100px;">📝 COMMENTS:</div>
                                        <div style="flex: 1; font-size: 12px;">{{ invoice.comment_txt }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        <div class="print-content">
                            <b-modal id="modal-image-viewer" size="lg" hide-footer centered title="Product Image">
                                <div class="text-center">
                                    <b-img
                                        :src="currentFullSizeImage"
                                        fluid
                                        alt="Full size product image"
                                        style="max-height: 70vh; object-fit: contain;"
                                    />
                                    <div class="mt-3">
                                        <strong>{{ currentProductName }}</strong>
                                    </div>
                                </div>
                                <template #modal-footer>
                                    <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-image-viewer')">
                                        Close
                                    </b-button>
                                </template>
                            </b-modal>
                            
                            <b-modal 
                                v-model="showFinanceDetails" 
                                title="Finance Agreements Details" 
                                size="lg" 
                                hide-footer
                                centered>
                                
                                <div v-if="allFinanceAgreements.length === 0">
                                    <p class="text-center text-muted">No finance agreements found.</p>
                                </div>
                                
                                <div v-for="(finance, index) in allFinanceAgreements" :key="index" 
                                     style="margin-bottom: 20px; padding: 15px; border: 1px solid #dee2e6; border-radius: 8px; background: #fff;">
                                    
                                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; border-bottom: 2px solid #007bff; padding-bottom: 8px;">
                                        <h6 style="color: #007bff; margin-bottom: 0;">
                                            {{ finance.company_name }}
                                        </h6>
                                        <span style="background: #28a745; color: white; padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                                            Agreement #{{ index + 1 }}
                                        </span>
                                    </div>
                                    
                                    <table style="width: 100%; font-size: 14px;">
                                        <tr>
                                            <td style="padding: 8px; width: 35%; background: #f8f9fa;"><strong>Amount Financed:</strong></td>
                                            <td style="padding: 8px;">${{ finance.amount.toFixed(2) }}</td>
                                        </tr>
                                        <tr v-if="finance.lease_id">
                                            <td style="padding: 8px; background: #f8f9fa;"><strong>Lease / Agreement ID:</strong></td>
                                            <td style="padding: 8px;">
                                                <code>{{ finance.lease_id }}</code>
                                            </td>
                                        </tr>
                                        <tr v-if="finance.misc_info">
                                            <td style="padding: 8px; background: #f8f9fa;"><strong>Miscellaneous Information:</strong></td>
                                            <td style="padding: 8px;">{{ finance.misc_info }}</td>
                                        </tr>
                                        <tr v-if="finance.company_phone">
                                            <td style="padding: 8px; background: #f8f9fa;"><strong>Finance Company Phone:</strong></td>
                                            <td style="padding: 8px;">
                                                <a :href="'tel:' + finance.company_phone">{{ finance.company_phone }}</a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 8px; background: #f8f9fa;"><strong>Tax Status:</strong></td>
                                            <td style="padding: 8px;">
                                                <span v-if="finance.charged_tax_itself" style="color: #28a745;">
                                                    ✓ Tax charged by finance company
                                                </span>
                                                <span v-else style="color: #6c757d;">
                                                    ✗ No tax charged by finance company
                                                </span>
                                            </td>
                                        </tr>
                                        <tr v-if="finance.ts">
                                            <td style="padding: 8px; background: #f8f9fa;"><strong>Transaction Date:</strong></td>
                                            <td style="padding: 8px;">{{ formatDate(finance.ts) }}</td>
                                        </tr>
                                    </table>
                                </div>
                                
                                <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #dee2e6;">
                                    <div style="display: flex; justify-content: space-between; align-items: center;">
                                        <div>
                                            <strong>Total Finance Amount:</strong>
                                            <span style="font-size: 18px; color: #28a745; margin-left: 10px;">
                                                ${{ getFinanceTotal.toFixed(2) }}
                                            </span>
                                        </div>
                                        <b-button variant="secondary" @click="showFinanceDetails = false">
                                            Close
                                        </b-button>
                                    </div>
                                </div>
                            </b-modal>

                            <b-modal id="modal-cs-ticket" @hide="reload_invoice" scrollable title="CS Ticket" size="lg" hide-footer>
                                <CustomerServiceTicket :viewdata="cs_item_data" />
                            </b-modal>

                            <b-modal id="modal-layaway-payments" @hide="reload_invoice" scrollable title="LAYAWAY PAYMENTS" size="xl" hide-footer>
                                <LayAwayPaymentsModal v-on:added-layaway-payments="close_layaway" :viewdata="invoice" :total_refunded_value="total_refunded_value" :financecompanies="finance_companies" />
                            </b-modal>

                            <div v-if="edit_invoice_action == true" style="margin-top: 30px; padding-top: 20px; margin-left: 130px; width:1600px; max-width: 1600px; padding: 0 20px;">
                                <EditInvoiceWrapper :viewdata="{invoice_id: invoice.invoice_id}" />
                            </div>
                            <div v-else>
                                <div class="hidden-print" style="width: 90%; margin-left: auto; margin-right: auto; margin-top: 15px;"><h5><b>CUSTOMER</b></h5></div>
                                <div style="display: block; margin-top: 10px; width: 90%; margin-left: auto; margin-right: auto;" class="separate hidden-print">
                                    <div>
                                        <b-card-group deck>
                                            <b-card style="width: 100%; border: 1px solid black; font-size: 13px;">
                                                <b-card-text class="d-flex flex-wrap" style="gap: 5px;">
                                                    <div style="width: 180px; display: inline-block; vertical-align: top;">
                                                        <div>FULLNAME</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                :disabled="packet.userDetails ? packet.userDetails.userrole != 'admin' : true" 
                                                                v-model="invoice.customer.fullname"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 180px; display: inline-block; vertical-align: top;">
                                                        <div>ADDRESS </div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.address_line_1"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 180px; display: inline-block; vertical-align: top;">
                                                        <div> ADDRESS 2 </div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.address_line_2"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 180px; display: inline-block; vertical-align: top;">
                                                        <div> FLOOR</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.floor"
                                                            ></b-input>
                                                        </div>
                                                    </div>

                                                    <div style="width: 120px; display: inline-block; vertical-align: top;">
                                                        <div>PHONE # 1</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.phone"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 120px; display: inline-block; vertical-align: top;">
                                                        <div>PHONE # 2</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.phone_2"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 180px; display: inline-block; vertical-align: top;">
                                                        <div>EMAIL</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.email"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 150px; display: inline-block; vertical-align: top;">
                                                        <div>ZIPCODE</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.zipcode"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 150px; display: inline-block; vertical-align: top;">
                                                        <div>CITY</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.city"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 80px; display: inline-block; vertical-align: top;">
                                                        <div>STATE</div>
                                                        <div>
                                                            <b-input 
                                                                size="sm" 
                                                                style="font-size: 13px;" 
                                                                v-model="invoice.customer.state"
                                                            ></b-input>
                                                        </div>
                                                    </div>
                                                    <div style="width: 150px; display: inline-block; vertical-align: top;">
                                                        <div>&nbsp;</div>
                                                        <div>
                                                            <b-button 
                                                                size="sm" 
                                                                variant="dark" 
                                                                class="hidden-print" 
                                                                :disabled="update_disabled" 
                                                                @click="update_invoice_customer"
                                                            >
                                                                UPDATE
                                                            </b-button>
                                                        </div>
                                                    </div>
                                                </b-card-text>
                                            </b-card>
                                        </b-card-group>
                                    </div>
                                </div>
                                
                                <div v-if="invoice.comment_txt" style="display: block; margin-top: 10px; width: 90%; margin-left: auto; margin-right: auto;" class="separate hidden-print">
                                    <div>
                                        <b-card-group deck>
                                            <b-card style="width: 100%; border: 1px solid black; background-color: #fff9e6;">
                                                <b-card-text>
                                                    <div style="display: flex; align-items: center; gap: 10px;">
                                                        <div style="font-weight: bold; color: #856404;">
                                                            📝 COMMENTS:
                                                        </div>
                                                        <div style="flex: 1;">
                                                            {{ invoice.comment_txt }}
                                                        </div>
                                                    </div>
                                                </b-card-text>
                                            </b-card>
                                        </b-card-group>
                                    </div>
                                </div>

                                <!-- Group by Parent Products -->
                                <div style="width: 90%; margin-left: auto; margin-right: auto;">
                                    <div style="overflow-x: auto;">
                                        <table style="width: 100%; margin-bottom: 10px;">
                                            <thead>
                                                <tr style="background: lightblue; color: black; border: 0.5px solid lightgray !important; ">
                                                    <th class="ipt_tdhr" style="min-width: 100px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">IMAGE</th>
                                                    <th class="ipt_tdhr" style="min-width: 150px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">SKU / VENDOR SKU</th>
                                                    <th class="ipt_tdhr" style="min-width: 170px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">PRODUCT DESC</th>
                                                    <th class="ipt_tdhr" style="min-width: 30px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">QTY</th>
                                                    <th class="ipt_tdhr" style="min-width: 50px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">CUBES</th>
                                                    <th class="ipt_tdhr" style="min-width: 80px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">UNIT PRICE</th>
                                                    <th class="ipt_tdhr" style="min-width: 50px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">UNIT DISCOUNT</th>
                                                    <th class="ipt_tdhr" style="min-width: 70px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">AMOUNT</th>
                                                    <th class="ipt_tdhr" style="min-width: 130px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">TYPE</th>
                                                    <th class="ipt_tdhr" style="min-width: 70px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">DELIVERY DATE</th>
                                                    <th class="ipt_tdhr" style="min-width: 130px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">DELIVERY LOCATION</th>
                                                    <th class="ipt_tdhr " style="min-width: 130px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">STATUS</th>
                                                    <th class="ipt_tdhr hidden-print" style="min-width: 100px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">AVAIL</th>
                                                    <th class="ipt_tdhr hidden-print" style="min-width: 100px; font-size: 13px; font-weight: bold; padding: 8px; text-align: center;">ACTION</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <template v-for="parentSku in orderedParentSkuList">
                                                    <template v-if="groupedByParent[parentSku] && groupedByParent[parentSku].length > 0">
                                                        
                                                        <!-- GROUP HEADER ROW -->
                                                        <template v-if="groupedByParent[parentSku][0] && 
                                                                        groupedByParent[parentSku][0].parent_product_sku && 
                                                                        groupedByParent[parentSku].length > 1">
                                                            
                                                            <tr :key="'group-header-'+parentSku" class="group-product-header" style="background: #e9ecef; border: 0.5px solid lightgray; border-bottom: none; border-right: 0.5px solid lightgray;">
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 0px; text-align: center; border-top: 0.5px solid lightgray ; border-left: 0.5px solid lightgray; border-bottom: 0.5px solid lightgray;">
                                                                    <div style="width: 90px; height: 90px; display: flex; align-items: center; justify-content: center; overflow: hidden; margin: 0 auto;">
                                                                        <div v-if="parentImageForGroup(parentSku)" class="image-container" style="width: 100%; height: 100%;">
                                                                            <img
                                                                                :src="parentImageForGroup(parentSku)"
                                                                                :alt="'Parent Product - ' + (groupedByParent[parentSku][0].parent_product_name || parentSku)"
                                                                                style="max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; cursor: pointer;"
                                                                                @error="handleImageError($event, {product_sku: parentSku})"
                                                                                @load="handleImageLoad($event, {product_sku: parentSku})"
                                                                                @click="showParentFullImage(groupedByParent[parentSku][0])"
                                                                                class="product-image"
                                                                            />
                                                                            <div class="image-overlay" @click="showParentFullImage(groupedByParent[parentSku][0])">
                                                                                🔍
                                                                            </div>
                                                                        </div>
                                                                        <div v-else class="no-image-placeholder" style="width: 60px; height: 50px;">
                                                                            <div style="color: #999; font-size: 10px;">No Image</div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; font-weight: bold; border: 0.5px solid lightgray;">
                                                                    <div style="display: flex; flex-direction: column; gap: 2px;">
                                                                        <div>{{ parentSku }}</div>
                                                                        <div v-if="groupedByParent[parentSku][0].parent_vendor_sku" style="font-size: 10px; color: #666;">
                                                                             {{ groupedByParent[parentSku][0].parent_vendor_sku }}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; font-weight: bold; border: 0.5px solid lightgray;">
                                                                    {{ groupedByParent[parentSku][0].parent_product_name || 'Group Product' }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; text-align: center; font-weight: bold; border: 0.5px solid lightgray;">
                                                                    {{ groupedByParent[parentSku][0].parent_product_quantity || 1 }}
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="background: #e9ecef; padding: 8px; text-align: center; font-weight: bold; border: 0.5px solid lightgray;">
                                                                    {{ calculateParentCubes(groupedByParent[parentSku]) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; text-align: center; font-weight: bold; border: 0.5px solid lightgray;">
                                                                    ${{ groupedByParent[parentSku][0].parent_product_selling_price || calculateParentUnitPrice(groupedByParent[parentSku]) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; text-align: center; font-weight: bold; border: 0.5px solid lightgray;">
                                                                    ${{ calculateParentTotalDiscount(groupedByParent[parentSku]) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; text-align: center; font-weight: bold; border: 0.5px solid lightgray;">
                                                                    ${{ calculateParentTotalAfterDiscount(groupedByParent[parentSku]) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; text-align: center; border: 0.5px solid lightgray;">
                                                                    <span v-if="isParentReturned(groupedByParent[parentSku])" style="color: #f44336; font-weight: bold;">
                                                                        ⚠️ RETURNED
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr" style="background: #e9ecef; padding: 8px; text-align: center; border: 0.5px solid lightgray;">
                                                                    &nbsp;
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="background: #e9ecef; padding: 8px; text-align: center; border: 0.5px solid lightgray;">
                                                                    &nbsp;
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="background: #e9ecef; padding: 8px; text-align: center; border: 0.5px solid lightgray;">
                                                                    &nbsp;
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="background: #e9ecef; padding: 8px; text-align: center; border: 0.5px solid lightgray ;">
                                                                    &nbsp;
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="background: #e9ecef; padding: 8px; text-align: center; border: 0.5px solid lightgray ;">
                                                                    &nbsp;
                                                                </td>
                                                            </tr>
                                                            
                                                            <!-- CHILD PRODUCT ROWS WITH CONDITIONAL OUTLINE -->
                                                            <tr v-for="(item, idx) in groupedByParent[parentSku]" 
                                                                :key="'group-'+parentSku+'-'+idx" 
                                                                class="product-row child-product-row"
                                                                :style="{
                                                                    border: is_returned(item) ? '3px solid #f44336' : (getSpiffValue(item) > 0 ? '2px solid #28a745' : '1px solid #dee2e6'),
                                                                    background: is_returned(item) ? '#ffebee' : ((idx % 2) ? 'white': '#f8f9fa'),
                                                                    borderRadius: getSpiffValue(item) > 0 ? '6px' : '0px',
                                                                    marginBottom: getSpiffValue(item) > 0 ? '4px' : '0px',
                                                                    boxShadow: getSpiffValue(item) > 0 ? '0 2px 8px rgba(40, 167, 69, 0.15)' : 'none',
                                                                    transition: 'all 0.3s ease'
                                                                }">
                                                                <td class="ipt_tdr" style="border: 0.5px solid lightgray ; text-align: center; padding: 8px; width: 200px; max-width: 200px;">
                                                                    <div style="text-align: center; margin-bottom: 5px; height: 60px; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                                                                        <div v-if="item.is_damaged && item.damage_history && item.damage_history.length > 0" 
                                                                             class="image-container" 
                                                                             style="width: 100%; height: 100%;">
                                                                            <img
                                                                                :src="getDamageHistoryImage(item)"
                                                                                :alt="'Damage Image - ' + (item.product_name || item.product_sku)"
                                                                                style="max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; cursor: pointer;"
                                                                                @error="handleDamageImageError($event, item)"
                                                                                @load="handleDamageImageLoad($event, item)"
                                                                                @click="showDamageFullImage(item)"
                                                                                class="product-image"
                                                                            />
                                                                            <div class="image-overlay" @click="showDamageFullImage(item)">
                                                                                🔍 DAMAGE
                                                                            </div>
                                                                        </div>
                                                                        <div v-else-if="getProductImage(item)" class="image-container" style="width: 100%; height: 100%;">
                                                                            <img
                                                                                :src="getProductImage(item)"
                                                                                :alt="item.product_name || item.product_sku"
                                                                                style="max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; cursor: pointer;"
                                                                                @error="handleImageError($event, item)"
                                                                                @load="handleImageLoad($event, item)"
                                                                                @click="showFullImage(item)"
                                                                                class="product-image"
                                                                            />
                                                                            <div class="image-overlay" @click="showFullImage(item)">
                                                                                🔍
                                                                            </div>
                                                                        </div>
                                                                        <div v-else class="no-image-placeholder">
                                                                            <div style="color: #999; font-size: 10px;">No Image</div>
                                                                        </div>
                                                                    </div>
                                                                    <div v-if="item.is_damaged" style="margin-top: 5px;" class="hidden-print">
                                                                        <b-badge variant="danger" style="font-size: 9px; padding: 3px 6px;">
                                                                            ⚠️ DAMAGED
                                                                        </b-badge>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; padding: 8px;">
                                                                    <div style="display: flex; flex-direction: column; gap: 2px;">
                                                                        <div style="display: inline-block; font-size: 13px;">
                                                                            {{ (item.product_sku || '').toUpperCase() }}
                                                                            <!-- OUTLINED SPIFF BADGE - ONLY VISIBLE ON SCREEN -->
                                                                            <span v-if="getSpiffValue(item) > 0" 
                                                                                  class="spiff-badge-outline screen-only" 
                                                                                  style="margin-left: 8px; background: transparent; color: #28a745; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: bold; display: inline-block; border: 2px solid #28a745; cursor: default;">
                                                                                💰 SPIFF ${{ getSpiffValue(item).toFixed(2) }}
                                                                            </span>
                                                                        </div>
                                                                        <div v-if="item.vendor_sku" style="font-size: 10px; color: #666;">
                                                                            {{ item.vendor_sku }}
                                                                        </div>
                                                                        <div v-if="hasBackorderLocsub(item)" style="color: red; font-weight: bold; font-size: 11px;" class="hidden-print">
                                                                            BACKORDER
                                                                        </div>
                                                                        <span v-if="(item.product_id < 0 || !item.product_id) && (item.is_special)" style="font-size: 11px;" class="hidden-print">
                                                                            <span><b-badge variant="primary" style="padding: 2px; font-size: 8px;">SPECIAL</b-badge></span>
                                                                            <div style="font-size: 9px; padding: 2px; background: lightyellow;">
                                                                                Special orders might take up to 8 weeks
                                                                            </div>
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; padding: 8px;">
                                                                    <div style="padding: 3px; font-size: 13px; font-weight: normal;">
                                                                        {{ (item.product_name || item.product_sku).toUpperCase() }}
                                                                        <div v-if="item.as_is === true" style="color: #d32f2f; font-weight: bold; font-size: 11px; margin-top: 2px;" class="hidden-print">
                                                                            ⚠️ SOLD AS-IS - NON-RETURNABLE
                                                                        </div>
                                                                        <div v-if="item.is_damaged" style="color: #d32f2f; font-weight: bold; font-size: 11px; margin-top: 2px; background: #ffebee; padding: 4px; border-radius: 3px; border-left: 3px solid #d32f2f;" class="hidden-print">
                                                                            🚫 CANNOT BE RETURNED OR REFUNDED - DAMAGED ITEM
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <div :style="{color: is_returned(item) ? '#f44336' : 'inherit', textDecoration: is_returned(item) ? 'line-through' : 'none'}">
                                                                        {{ item.quantity - (item.refunded_so_far || 0) }}
                                                                        <span v-if="is_returned(item)" style="font-size: 10px; color: #f44336; margin-left: 5px;" class="hidden-print">(RETURNED)</span>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    {{ item.cubes || 0.0 }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 80px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    ${{ parseFloat(item.selling_price || 0).toFixed(2) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 60px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    ${{ parseFloat(item.unit_discount_value || 0).toFixed(2) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    ${{ parseFloat(((item.selling_price || 0) * (item.quantity || 0)) - (item.discount_offered || 0)).toFixed(2) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 60px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        <b-form-select 
                                                                            :options="order_types" 
                                                                            size="md" 
                                                                            style="font-size: 12px;" 
                                                                            v-model="item.order_type" 
                                                                            @change="(newValue, oldValue) => onOrderTypeChange(item, newValue, oldValue)" 
                                                                            :disabled="is_returned(item)"
                                                                            :title="getOrderTypeHistoryTooltip(item)"
                                                                        ></b-form-select>
                                                                        <span style="font-size: 10px;" v-if="item.source === 'store'">
                                                                            {{ getLocationDisplay(item.original_locsub) || item.original_locsub || 'N/A' }}
                                                                        </span>
                                                                        <div v-if="item.order_type_history && item.order_type_history.length > 0" 
                                                                             style="font-size: 9px; color: #6c757d; margin-top: 3px; cursor: help;"
                                                                             :title="getOrderTypeHistoryTooltip(item)">
                                                                            🔄 Changed {{ item.order_type_history.length }} time(s)
                                                                        </div>
                                                                    </span>
                                                                    <span class="print-only" style="display: none;">
                                                                        <span v-if="item.source === 'store'">
                                                                            {{ formatOrderType(item.order_type) }}
                                                                            {{ getLocationDisplay(item.original_locsub) || item.original_locsub || 'N/A' }}
                                                                        </span>
                                                                        <span v-else>
                                                                            {{ formatOrderType(item.order_type) }}
                                                                        </span>
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 70px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        <div style="display: flex; flex-direction: column; gap: 2px;">
                                                                            <date-picker v-model="item.delivery_date"
                                                                                format="YYYY-MM-DD"
                                                                                valueType="format"
                                                                                style="padding: 4px; font-size: 12px; text-align: center; border: 0.5px solid lightgray; width: 125px;"
                                                                                @change="update_item_level(item)"
                                                                                :disabled="is_returned(item)"
                                                                            ></date-picker>
                                                                            <div v-if="item.last_delivery_date && item.last_delivery_date !== item.delivery_date" 
                                                                                 style="font-size: 10px; color: #ff9800; margin-top: 2px; text-align: center;">
                                                                                <span style="font-weight: bold;">PREVIOUS:</span> {{ item.last_delivery_date }}
                                                                            </div>
                                                                        </div>
                                                                    </span>
                                                                    <span class="print-only" style="display: none; display: flex; flex-direction: column; align-items: center;">
                                                                        <div>{{ item.delivery_date || '' }}</div>
                                                                        <div style="font-size: 10px; color: #666;">{{ getDayOfWeek(item.delivery_date) }}</div>
                                                                        <div v-if="item.last_delivery_date && item.last_delivery_date !== item.delivery_date" 
                                                                             style="font-size: 9px; color: #ff9800;">
                                                                            Prev: {{ item.last_delivery_date }}
                                                                        </div>
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        {{ getLocationDisplay(item.drop_locsub) || item.drop_locsub || 'N/A' }}
                                                                    </span>
                                                                    <span class="print-only" style="display: none;">
                                                                        {{ getLocationDisplay(item.drop_locsub) || item.drop_locsub || 'N/A' }}
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr " style="border: 0.5px solid lightgray ; width: 120px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        <b-form-select :options="states" v-model="item.state" style="font-size: 13px;" size="sm" :disabled="true"></b-form-select>
                                                                    </span>
                                                                    <span class="print-only" style="display: none;">
                                                                        {{ getStateDisplayName(item.state) }}
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="border: 0.5px solid lightgray ; background: lightyellow; padding: 8px; text-align: center;">
                                                                    {{ item.avail || '' }}
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="border: 0.5px solid lightgray ; width: 100px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span v-if="is_returned(item)">
                                                                        <b-alert show size="sm" variant="secondary">RETURNED</b-alert>
                                                                    </span>
                                                                    <span v-else>
                                                                        <div style="display: flex; flex-direction: column; gap: 5px; align-items: center;">
                                                                            <b-button 
                                                                                variant="outline-info" 
                                                                                size="sm" 
                                                                                @click="view_cs_issue(item)"
                                                                                style="width: 80px; font-size: 11px;"
                                                                                title="View or create claim"
                                                                            >
                                                                                VIEW
                                                                            </b-button>
                                                                            <b-button variant="outline-primary" size="sm" @click="load_cs_issues(item)" style="width: 80px;">
                                                                                ISSUES
                                                                                <span v-if="hasTicket(item)">1</span>
                                                                            </b-button>
                                                                            <b-button 
                                                                                v-if="item.as_is === true && item.as_is_image_url"
                                                                                variant="outline-success" 
                                                                                size="sm" 
                                                                                @click="showAsIsImage(item)"
                                                                                style="width: 80px; font-size: 11px;"
                                                                                title="View As-Is Image"
                                                                            >
                                                                                VIEW
                                                                            </b-button>
                                                                        </div>
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        </template>
                                                        
                                                        <!-- REGULAR PRODUCT ROWS WITH CONDITIONAL OUTLINE -->
                                                        <template v-else>
                                                            <tr v-for="(item, idx) in groupedByParent[parentSku]" 
                                                                :key="'regular-'+parentSku+'-'+idx" 
                                                                class="product-row"
                                                                :style="{
                                                                    border: is_returned(item) ? '3px solid #f44336' : (getSpiffValue(item) > 0 ? '2px solid #28a745' : '1px solid #dee2e6'),
                                                                    background: is_returned(item) ? '#ffebee' : ((idx % 2) ? 'white': '#f8f9fa'),
                                                                    borderRadius: getSpiffValue(item) > 0 ? '6px' : '0px',
                                                                    marginBottom: getSpiffValue(item) > 0 ? '4px' : '0px',
                                                                    boxShadow: getSpiffValue(item) > 0 ? '0 2px 8px rgba(40, 167, 69, 0.15)' : 'none',
                                                                    transition: 'all 0.3s ease'
                                                                }">
                                                                <td class="ipt_tdr" style="border: 0.5px solid lightgray ; text-align: center; padding: 8px; width: 200px; max-width: 200px;">
                                                                    <div style="text-align: center; margin-bottom: 5px; height: 60px; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                                                                        <div v-if="item.is_damaged && item.damage_history && item.damage_history.length > 0" 
                                                                             class="image-container" 
                                                                             style="width: 100%; height: 100%;">
                                                                            <img
                                                                                :src="getDamageHistoryImage(item)"
                                                                                :alt="'Damage Image - ' + (item.product_name || item.product_sku)"
                                                                                style="max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; cursor: pointer;"
                                                                                @error="handleDamageImageError($event, item)"
                                                                                @load="handleDamageImageLoad($event, item)"
                                                                                @click="showDamageFullImage(item)"
                                                                                class="product-image"
                                                                            />
                                                                            <div class="image-overlay" @click="showDamageFullImage(item)">
                                                                                🔍 DAMAGE
                                                                            </div>
                                                                        </div>
                                                                        <div v-else-if="getProductImage(item)" class="image-container" style="width: 100%; height: 100%;">
                                                                            <img
                                                                                :src="getProductImage(item)"
                                                                                :alt="item.product_name || item.product_sku"
                                                                                style="max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; cursor: pointer;"
                                                                                @error="handleImageError($event, item)"
                                                                                @load="handleImageLoad($event, item)"
                                                                                @click="showFullImage(item)"
                                                                                class="product-image"
                                                                            />
                                                                            <div class="image-overlay" @click="showFullImage(item)">
                                                                                🔍
                                                                            </div>
                                                                        </div>
                                                                        <div v-else class="no-image-placeholder">
                                                                            <div style="color: #999; font-size: 10px;">No Image</div>
                                                                        </div>
                                                                    </div>
                                                                    <div v-if="item.is_damaged" style="margin-top: 5px;" class="hidden-print">
                                                                        <b-badge variant="danger" style="font-size: 9px; padding: 3px 6px;">
                                                                            ⚠️ DAMAGED
                                                                        </b-badge>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; padding: 8px;">
                                                                    <div style="display: flex; flex-direction: column; gap: 2px;">
                                                                        <div style="display: inline-block; font-size: 13px;">
                                                                            {{ (item.product_sku || '').toUpperCase() }}
                                                                            <!-- OUTLINED SPIFF BADGE - ONLY VISIBLE ON SCREEN -->
                                                                            <span v-if="getSpiffValue(item) > 0" 
                                                                                  class="spiff-badge-outline screen-only" 
                                                                                  style="margin-left: 8px; background: transparent; color: #28a745; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: bold; display: inline-block; border: 2px solid #28a745; cursor: default;">
                                                                                💰 SPIFF ${{ getSpiffValue(item).toFixed(2) }}
                                                                            </span>
                                                                        </div>
                                                                        <div v-if="item.vendor_sku" style="font-size: 10px; color: #666;">
                                                                            {{ item.vendor_sku }}
                                                                        </div>
                                                                        <div v-if="hasBackorderLocsub(item)" style="color: red; font-weight: bold; font-size: 11px;" class="hidden-print">
                                                                            BACKORDER
                                                                        </div>
                                                                        <span v-if="(item.product_id < 0 || !item.product_id) && (item.is_special)" style="font-size: 11px;" class="hidden-print">
                                                                            <span><b-badge variant="primary" style="padding: 2px; font-size: 8px;">SPECIAL</b-badge></span>
                                                                            <div style="font-size: 9px; padding: 2px; background: lightyellow;">
                                                                                Special orders might take up to 8 weeks
                                                                            </div>
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; padding: 8px;">
                                                                    <div style="padding: 3px; font-size: 13px; font-weight: normal;">
                                                                        {{ (item.product_name || item.product_sku).toUpperCase() }}
                                                                        <div v-if="item.as_is === true" style="color: #d32f2f; font-weight: bold; font-size: 11px; margin-top: 2px;" class="hidden-print">
                                                                            ⚠️ SOLD AS-IS - NON-RETURNABLE
                                                                        </div>
                                                                        <div v-if="item.is_damaged" style="color: #d32f2f; font-weight: bold; font-size: 11px; margin-top: 2px; background: #ffebee; padding: 4px; border-radius: 3px; border-left: 3px solid #d32f2f;" class="hidden-print">
                                                                            🚫 CANNOT BE RETURNED OR REFUNDED - DAMAGED ITEM
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <div :style="{color: is_returned(item) ? '#f44336' : 'inherit', textDecoration: is_returned(item) ? 'line-through' : 'none'}">
                                                                        {{ (item.quantity || 0) - (item.refunded_so_far || 0) }}
                                                                        <span v-if="is_returned(item)" style="font-size: 10px; color: #f44336; margin-left: 5px;" class="hidden-print">(RETURNED)</span>
                                                                    </div>
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    {{ item.cubes || 0.0 }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 80px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    ${{ parseFloat(item.selling_price || 0).toFixed(2) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 60px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    ${{ parseFloat(item.unit_discount_value || 0).toFixed(2) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    ${{ parseFloat(((item.selling_price || 0) * (item.quantity || 0)) - (item.discount_offered || 0)).toFixed(2) }}
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 60px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        <b-form-select :options="order_types" size="md" style="font-size: 12px;" v-model="item.order_type" @change="update_item_level(item)" :disabled="is_returned(item)"></b-form-select>
                                                                        <span style="font-size: 10px;" v-if="item.source === 'store'">
                                                                            {{ getLocationDisplay(item.original_locsub) || item.original_locsub || 'N/A' }}
                                                                        </span>
                                                                    </span>
                                                                    <span class="print-only" style="display: none;">
                                                                        <span v-if="item.source === 'store'">
                                                                            {{ formatOrderType(item.order_type) }}
                                                                            {{ getLocationDisplay(item.original_locsub) || item.original_locsub || 'N/A' }}
                                                                        </span>
                                                                        <span v-else>
                                                                            {{ formatOrderType(item.order_type) }}
                                                                        </span>
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; width: 70px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        <div style="display: flex; flex-direction: column; gap: 2px;">
                                                                            <date-picker v-model="item.delivery_date"
                                                                                format="YYYY-MM-DD"
                                                                                valueType="format"
                                                                                style="padding: 4px; font-size: 12px; text-align: center; border: 0.5px solid lightgray; width: 125px;"
                                                                                @change="update_item_level(item)"
                                                                                :disabled="is_returned(item)"
                                                                            ></date-picker>
                                                                            <div v-if="item.last_delivery_date && item.last_delivery_date !== item.delivery_date" 
                                                                                style="font-size: 10px; color: #ff9800; margin-top: 2px; text-align: center;">
                                                                                <span style="font-weight: bold;">PREVIOUS:</span> {{ item.last_delivery_date }}
                                                                            </div>
                                                                        </div>
                                                                    </span>
                                                                    <span class="print-only" style="display: none; display: flex; flex-direction: column; align-items: center;">
                                                                        <div>{{ item.delivery_date || '' }}</div>
                                                                        <div style="font-size: 10px; color: #666;">{{ getDayOfWeek(item.delivery_date) }}</div>
                                                                        <div v-if="item.last_delivery_date && item.last_delivery_date !== item.delivery_date" 
                                                                            style="font-size: 9px; color: #ff9800;">
                                                                            Prev: {{ item.last_delivery_date }}
                                                                        </div>
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr" style="border: 0.5px solid lightgray ; font-weight: normal; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        {{ getLocationDisplay(item.drop_locsub) || item.drop_locsub || 'N/A' }}
                                                                    </span>
                                                                    <span class="print-only" style="display: none;">
                                                                        {{ getLocationDisplay(item.drop_locsub) || item.drop_locsub || 'N/A' }}
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr " style="border: 0.5px solid lightgray ; width: 120px; padding: 8px; text-align: center;">
                                                                    <span class="screen-only">
                                                                        <b-form-select :options="states" v-model="item.state" style="font-size: 13px;" size="sm" :disabled="true"></b-form-select>
                                                                    </span>
                                                                    <span class="print-only" style="display: none;">
                                                                        {{ getStateDisplayName(item.state) }}
                                                                    </span>
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="border: 0.5px solid lightgray ; background: lightyellow; padding: 8px; text-align: center;">
                                                                    {{ item.avail || '' }}
                                                                </td>
                                                                <td class="ipt_tdhr hidden-print" style="border: 0.5px solid lightgray ; width: 100px; font-size: 13px; padding: 8px; text-align: center;">
                                                                    <span v-if="is_returned(item)">
                                                                        <b-alert show size="sm" variant="secondary">RETURNED</b-alert>
                                                                    </span>
                                                                    <span v-else>
                                                                        <div style="display: flex; flex-direction: column; gap: 5px; align-items: center;">
                                                                            <b-button 
                                                                                variant="outline-info" 
                                                                                size="sm" 
                                                                                @click="view_cs_issue(item)"
                                                                                style="width: 80px; font-size: 11px;"
                                                                                title="View or create claim"
                                                                            >
                                                                                VIEW
                                                                            </b-button>
                                                                            <b-button variant="outline-primary" size="sm" @click="load_cs_issues(item)" style="width: 80px;">
                                                                                ISSUES
                                                                                <span v-if="item.cs_ticket_id != null">1</span>
                                                                            </b-button>
                                                                            <b-button 
                                                                                v-if="item.as_is === true && item.as_is_image_url"
                                                                                variant="outline-success" 
                                                                                size="sm" 
                                                                                @click="showAsIsImage(item)"
                                                                                style="width: 80px; font-size: 11px;"
                                                                                title="View As-Is Image"
                                                                            >
                                                                                VIEW
                                                                            </b-button>
                                                                        </div>
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        </template>
                                                    </template>
                                                </template>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- PAYMENT SUMMARY SECTION -->
                                <div class="separate2" style="margin-top: 15px; width: 50%; padding: 20px; margin-left: auto; margin-right: auto;">
                                    <div style="margin-left: auto; margin-right: auto; margin-top: 15px;">
                                        <h5 style="text-align: center; margin-bottom: 20px;"><b>PAYMENT SUMMARY</b></h5>
                                    </div>
       
                                    <table style="width: 100%; border-collapse: separate; border-spacing: 20px; font-size: 13px;">
                                        <thead>
                                            <tr style="background: lightblue; color: black;">
                                                <th style="border: 0.5px solid lightgray; padding: 10px; text-align: center; width: 33.33%;">EXTRA CHARGES</th>
                                                <th style="border: 0.5px solid lightgray; padding: 10px; text-align: center; width: 33.33%;">TOTAL CHARGES</th>
                                                <th style="border: 0.5px solid lightgray; padding: 10px; text-align: center; width: 33.33%;">PAYMENTS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style="padding: 8px; vertical-align: top;">
                                                    <table style="width: 100%; border-collapse: collapse;">
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Delivery</div> ${{ parseFloat(invoice.delivery_charges || 0).toFixed(2) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Setup</div> ${{ parseFloat(invoice.setup_charges || 0).toFixed(2) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Warranty</div> ${{ parseFloat(invoice.warranty_charges || 0).toFixed(2) }}</td>
                                                        </tr>
                                                    </table>
                                                </td>
                                                <td style="padding: 8px; vertical-align: top;">
                                                    <table style="width: 100%; border-collapse: collapse;">
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;">
                                                                <div style="font-weight: bold;">Total discount</div> 
                                                                ${{ getTotalDiscount().toFixed(2) }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;">
                                                                <div style="font-weight: bold;">Order Total</div> 
                                                                ${{ getOrderTotal().toFixed(2) }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;">
                                                                <div style="font-weight: bold;">Extra Charges</div> 
                                                                ${{ getChargesTotal().toFixed(2) }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;">
                                                                <div style="font-weight: bold;">Taxable Total</div>   
                                                                ${{ getTaxableSubtotal().toFixed(2) }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;">
                                                                <div style="font-weight: bold;">Sales Tax</div> 
                                                                ${{ getSalesTaxAmount().toFixed(2) }}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;">
                                                                <div style="font-weight: bold;">Grand Total</div>  
                                                                ${{ getGrandTotal().toFixed(2) }}
                                                            </td>
                                                        </tr>
                                                    </table>
                                                </td>
                                                <td style="padding: 8px; vertical-align: top;">
                                                    <table style="width: 100%; border-collapse: collapse;">
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Store Credit</div> ${{ getPaymentByMethod('store_credit') || 0.00 }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Cash</div> ${{ getPaymentByMethod('cash') || 0.00 }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Credit Card</div> ${{ getPaymentByMethod('cc') || 0.00 }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;">
                                                                <div style="font-weight: bold;">
                                                                    <template v-if="hasTaxExemptPayment">
                                                                        TAX EXEMPT
                                                                        <span v-if="getTaxExemptCompanyNames.length > 0">
                                                                            ({{ getTaxExemptCompanyNames.join(', ') }})
                                                                        </span>
                                                                    </template>
                                                                    <template v-else>
                                                                        Finance 
                                                                        <span v-if="getFinanceCompanyNames.length > 0">
                                                                            ({{ getFinanceCompanyNames.join(', ') }})
                                                                        </span>
                                                                    </template>
                                                                    <span v-if="allFinanceAgreements.length > 1" style="font-size: 11px; font-weight: normal;">
                                                                        - {{ allFinanceAgreements.length }} agreements
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    ${{ getTaxExemptOrFinanceTotal.toFixed(2) }}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <!-- PARENT PAYMENT METHOD - EXCHANGE INVOICE -->
                                                        <tr v-if="shouldShowParentPayment">
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px; background: #f0f8ff;">
                                                                <div style="font-weight: bold; color: #0056b3;">
                                                                    Parent Payment 
                                                                    <span style="font-size: 11px; font-weight: normal; color: #666;">
                                                                        ({{ invoice.parent_invoice_id }})
                                                                    </span>
                                                                </div>
                                                                <div style="color: #0056b3; font-weight: bold;">
                                                                    ${{ parentPaymentTotal.toFixed(2) }}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <!-- PARENT PAYMENT ALLOCATION DETAILS -->
                                                        <tr v-if="shouldShowParentPayment && getParentPaymentMethodsForItems.length > 0">
                                                            <td colspan="3" style="padding: 8px; border: 0.5px solid lightgray; background: #f8f9fa;">
                                                                <div style="font-size: 12px; color: #666; padding: 8px; border-radius: 4px;">
                                                                    <div style="font-weight: bold; margin-bottom: 5px; color: #333;">
                                                                        Parent Payment Details (Exchange Items Total: ${{ getExchangeTotal.toFixed(2) }})
                                                                        <span style="font-weight: normal; font-size: 11px; color: #666; margin-left: 8px;">
                                                                            (Parent Invoice Total: ${{ parentInvoiceTotal.toFixed(2) }})
                                                                        </span>
                                                                    </div>
                                                                    <div v-for="pm in getParentPaymentMethodsForItems" :key="pm.id || pm.name" 
                                                                        style="display: flex; justify-content: space-between; padding: 2px 0; border-bottom: 1px dashed #e0e0e0;">
                                                                        <span>{{ (pm.value || '').toUpperCase() }} - {{ pm.name }}</span>
                                                                        <span style="font-weight: bold;">${{ pm.display_amount }}</span>
                                                                    </div>
                                                                    <div style="display: flex; justify-content: space-between; padding: 4px 0; margin-top: 4px; border-top: 2px solid #ddd; font-weight: bold; color: #0056b3;">
                                                                        <span>Total Allocated to Exchange</span>
                                                                        <span>${{ allocatedParentPaymentTotal.toFixed(2) }}</span>
                                                                    </div>
                                                                    <div v-if="getExchangeTotal < parentPaymentTotal" 
                                                                        style="display: flex; justify-content: space-between; padding: 2px 0; margin-top: 2px; color: #856404; font-size: 11px;">
                                                                        <span>Remaining Parent Payment (Store Credit Available)</span>
                                                                        <span>${{ (parentPaymentTotal - getExchangeTotal).toFixed(2) }}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Sub Total</div> ${{ getSubTotal().toFixed(2) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style="display:flex; justify-content: space-between; padding:20px; border: 0.5px solid lightgray; padding: 4px;"><div style="font-weight: bold;">Total Paid</div>${{ get_pms_total().toFixed(2) }}</td>
                                                        </tr>
                                                    </table>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    
                                    <!-- Finance Agreements Section -->
                                    <div v-if="allFinanceAgreements.length > 0" 
                                        style="margin-bottom: 20px; padding: 15px; border-radius: 8px; background: #f8f9fa; border: 1px solid #dee2e6;">
                                        
                                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                                            <h6 style="margin-bottom: 0; font-weight: bold; color: #2c3e50;">
                                                🏦 FINANCE AGREEMENTS 
                                                <span style="font-size: 12px; color: #6c757d;">({{ allFinanceAgreements.length }})</span>
                                            </h6>
                                            <b-button 
                                                v-if="allFinanceAgreements.length > 1" 
                                                size="sm" 
                                                variant="link" 
                                                @click="showFinanceDetails = true"
                                                class="p-0 hidden-print">
                                                View Details
                                            </b-button>
                                        </div>
                                        
                                        <div class="screen-only">
                                            <div v-for="(finance, index) in allFinanceAgreements" :key="index" 
                                                style="margin-bottom: 8px; padding: 8px 12px; background: white; border-radius: 4px; border-left: 3px solid #007bff;">
                                                
                                                <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
                                                    <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
                                                        <span style="font-weight: bold; color: #007bff; font-size: 13px;">
                                                            {{ finance.company_name }}
                                                        </span>
                                                        <span v-if="finance.lease_id" style="font-size: 11px; color: #6c757d;">
                                                            Lease: {{ finance.lease_id }}
                                                        </span>
                                                        <span v-if="finance.company_phone" style="font-size: 11px; color: #6c757d;">
                                                            Tel: {{ finance.company_phone }}
                                                        </span>
                                                        <span style="font-size: 10px;">
                                                            <span v-if="finance.charged_tax_itself" 
                                                                style="background: #28a745; color: white; padding: 2px 6px; border-radius: 10px;">
                                                                Tax Inc
                                                            </span>
                                                            <span v-else 
                                                                style="background: #6c757d; color: white; padding: 2px 6px; border-radius: 10px;">
                                                                No Tax
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div style="font-size: 14px; font-weight: bold; color: #28a745;">
                                                        ${{ finance.amount.toFixed(2) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div class="print-only" style="display: none;">
                                            <table style="width: 100%; font-size: 12px;">
                                                <thead>
                                                    <tr style="background-color: #e9ecef; border: 1px solid #000;">
                                                        <th style="border: 0.5px solid lightgray; padding: 8px; text-align: left; font-weight: bold;">Finance Company</th>
                                                        <th style="border: 0.5px solid lightgray; padding: 8px; text-align: left; font-weight: bold;">Lease/Agreement ID</th>
                                                        <th style="border: 0.5px solid lightgray; padding: 8px; text-align: left; font-weight: bold;">Phone Number</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="(finance, index) in allFinanceAgreements" :key="index" style="border: 1px solid #000;">
                                                        <td style="border: 0.5px solid lightgray; padding: 6px;">{{ finance.company_name || 'N/A' }}</td>
                                                        <td style="border: 0.5px solid lightgray; padding: 6px;">{{ finance.lease_id || 'N/A' }}</td>
                                                        <td style="border: 0.5px solid lightgray; padding: 6px;">{{ finance.company_phone || 'N/A' }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        <div class="screen-only" style="margin-top: 12px; padding-top: 8px; border-top: 1px dashed #dee2e6; text-align: right;">
                                            <strong style="font-size: 12px;">Total Financed:</strong>
                                            <span style="font-size: 14px; font-weight: bold; color: #28a745; margin-left: 8px;"> 
                                                ${{ getFinanceTotal.toFixed(2) }}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <!-- PAY BUTTON - HIDDEN FOR EX INVOICES -->
                                    <b-button 
                                        style="display:flex; justify-content: right;" 
                                        size="sm" 
                                        v-if="(invoice.is_layaway || get_total_balance() > 0) && invoice.layaway_refunded_invoice_id == null && invoice.invoice_type !== 'EX'" 
                                        variant="outline-dark" 
                                        class="hidden-print" 
                                        @click="open_layaway_payment_modal">
                                        PAY
                                    </b-button>
                                    
                                    <!-- Enhanced Balance Display - HIDDEN FOR EX INVOICES -->
                                    <div v-if="(invoice.is_layaway || get_total_balance() > 0) && invoice.layaway_refunded_invoice_id == null && invoice.invoice_type !== 'EX'" 
                                        style="display:flex; flex-direction: column; align-items: flex-end; margin-top: 10px;">
                                        
                                        <div style="display: flex; justify-content: space-between; width: 280px; padding: 4px;">
                                            <span style="font-weight: bold;">Current Balance:</span>
                                            <span style="font-weight: bold;">${{ (get_total_balance() || 0).toFixed(2) }}</span>
                                        </div>
                                        
                                        <div v-if="predictedSalesTax > 0" style="display: flex; justify-content: space-between; width: 280px; padding: 4px; border-top: 1px dashed #ddd;">
                                            <span style="font-weight: bold; color: #ed8936;">
                                                Predicted Sales Tax ({{ (parseFloat(invoice.tax_perc) * 100).toFixed(2) }}%):
                                            </span>
                                            <span style="font-weight: bold; color: #ed8936;">${{ predictedSalesTax.toFixed(2) }}</span>
                                        </div>
                                        
                                        <div v-if="predictedSalesTax > 0" style="display: flex; justify-content: space-between; width: 280px; padding: 8px 4px; margin-top: 5px; background: #f0f0f0; border-radius: 4px;">
                                            <span style="font-weight: bold; color: #2c3e50;">Total to Pay:</span>
                                            <span style="font-weight: bold; color: #2c3e50; font-size: 1.1em;">${{ totalWithPredictedTax.totalToPay.toFixed(2) }}</span>
                                        </div>
                                        
                                        <div v-else style="display: flex; justify-content: space-between; width: 280px; padding: 4px;">
                                            <span style="font-weight: bold; color: red;">Balance:</span>
                                            <span style="font-weight: bold; color: red;">${{ (get_total_balance() || 0).toFixed(2) }}</span>
                                        </div>
                                        
                                        <div v-if="parseFloat(invoice.tax_perc) > 0" style="display: flex; justify-content: space-between; width: 280px; padding: 2px 4px; font-size: 11px; color: #666;">
                                            <span>Tax Rate:</span>
                                            <span>{{ (parseFloat(invoice.tax_perc) * 100).toFixed(2) }}%</span>
                                        </div>
                                    </div>
                                    
                                    <!-- Layaway Payment History Section -->
                                    <div v-if="invoice.is_layaway" style="margin-top: 30px;">
                                        <h5 style="text-align: center; margin-bottom: 15px;"><b>LAYAWAY PAYMENT HISTORY</b></h5>
                                        
                                        <table style="width: 100%; border-collapse: collapse; border: 0.5px solid lightgrey; font-size: 13px;">
                                            <thead>
                                                <tr style="background: lightblue; color: black;">
                                                    <th v-for="n in 4" :key="n" style="border: 0.5px solid lightgray; padding: 10px; text-align: center;">
                                                        PAYMENT {{ n }}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td v-for="(payment, index) in getLayawayPayments().slice(0,4)" :key="'method-'+index" 
                                                        style="border: 0.5px solid lightgray; padding: 8px; text-align: center; font-weight: bold;">
                                                        {{ (payment.method || '').toUpperCase() }}
                                                    </td>
                                                    <td v-for="n in Math.max(0, 4 - (getLayawayPayments().length || 0))" :key="'empty-method-'+n" 
                                                        style="border: 1px solid lightgray; padding: 8px; text-align: center;">
                                                        &nbsp;
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td v-for="(payment, index) in getLayawayPayments().slice(0,4)" :key="'amount-'+index" 
                                                        style="border: 0.5px solid lightgray; padding: 8px; text-align: center;">
                                                        ${{ parseFloat(payment.amount || 0).toFixed(2) }}
                                                    </td>
                                                    <td v-for="n in Math.max(0, 4 - (getLayawayPayments().length || 0))" :key="'empty-amount-'+n" 
                                                        style="border: 0.5px solid lightgray; padding: 8px; text-align: center;">
                                                        &nbsp;
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td v-for="(payment, index) in getLayawayPayments().slice(0,4)" :key="'date-'+index" 
                                                        style="border: 0.5px solid lightgray; padding: 8px; text-align: center;">
                                                        {{ payment.ts ? payment_ts_format(payment.ts).split(' ')[0] : '' }}
                                                    </td>
                                                    <td v-for="n in Math.max(0, 4 - (getLayawayPayments().length || 0))" :key="'empty-date-'+n" 
                                                        style="border: 0.5px solid lightgray; padding: 8px; text-align: center;">
                                                        &nbsp;
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- Refund Section -->
                                <div style="width: 98%; display: block; margin-top: 10px; margin-bottom: 50px; margin-left: auto; margin-right: auto;">
                                    <div>
                                        <div style="display: block; margin-top: 10px; width: 90%; margin-left: auto; margin-right: auto;">
                                            <b-alert show variant="secondary" v-if="invoice.items && invoice.items[0] && invoice.items[0].modifiable && refunds.items_to_be_refunded.length > 0" class="hidden-print">
                                                <div><h5><b>ACTIVE REFUND</b></h5></div>
                                                <table style="width: 100%; font-size: 13px;">
                                                    <thead>
                                                        <tr style="background: white; color: black; font-weight: bold; font-size: 13px;">
                                                            <th style="border: 1px solid; text-align: center;">PARENT PRODUCT</th>
                                                            <th style="border: 1px solid; text-align: center;">SKU</th>
                                                            <th style="border: 1px solid; text-align: center;">PRODUCT DESC</th>
                                                            <th style="border: 1px solid; text-align: center;">QTY</th>
                                                            <th style="border: 1px solid; text-align: center;">REFUNDED QTY</th>
                                                            <th style="border: 1px solid; text-align: center;">REMAINING QTY</th>
                                                            <th style="border: 1px solid; text-align: center;">UNIT PRICE</th>
                                                            <th style="border: 1px solid; text-align: center;">UNIT DISCOUNT</th>
                                                            <th style="border: 1px solid; text-align: center;">RETURN QTY</th>
                                                            <th style="border: 1px solid; text-align: center;">RETURN AMOUNT</th>
                                                            <th style="border: 1px solid; text-align: center;">REASON</th>
                                                            <th style="border: 1px solid; text-align: center;">DELIVERED</th>
                                                            <th style="border: 1px solid; text-align: center;">RETURN LOC</th>
                                                            <th style="border: 1px solid; text-align: center;">ACTION</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(rtitem, rtidx) in refunds.items_to_be_refunded" :key="rtidx" style="background: white; color: black; font-weight: normal; font-size: 13px;">
                                                            <td style="border: 1px solid; text-align: center;">{{ rtitem.parent_product_sku }}</td>
                                                            <td style="border: 1px solid; text-align: center;">{{ rtitem.product_sku }}</td>
                                                            <td style="border: 1px solid; text-align: center;">{{ rtitem.product_name }}</td>
                                                            <td style="border: 1px solid; text-align: center;">{{ rtitem.quantity }}</td>
                                                            <td style="border: 1px solid; text-align: center;">{{ rtitem.refunded_so_far }}</td>
                                                            <td style="border: 1px solid; text-align: center;">{{ rtitem.quantity - rtitem.refunded_so_far }}</td>
                                                            <td style="border: 1px solid; text-align: center;">{{ (rtitem.selling_price || 0).toFixed(2) }}</td>
                                                            <td style="border: 1px solid; text-align: center;">{{ (rtitem.unit_discount_value || 0).toFixed(2) }}</td>
                                                            <td style="border: 1px solid; text-align: center;">
                                                                <b-form-select size="sm" v-model="rtitem.return_quantity" :options="get_quantity_dd(rtitem.quantity - rtitem.refunded_so_far)"></b-form-select>
                                                            </td>
                                                            <td style="border: 1px solid; text-align: center;">
                                                                {{ ( (rtitem.selling_price * rtitem.return_quantity) -  (rtitem.unit_discount_value * rtitem.return_quantity) ).toFixed(2) }}
                                                            </td>
                                                            <td style="border: 1px solid; text-align: center;">
                                                                <b-form-select size="sm" style="font-size: 13px;" v-model="rtitem.return_reason" :options="return_reasons"></b-form-select>
                                                            </td>
                                                            <td style="border: 1px solid; text-align: center;">
                                                                <b-form-select size="sm" style="font-size: 13px;" v-model="rtitem.delivered" :options="is_delivered"></b-form-select>
                                                            </td>
                                                            <td style="border: 1px solid; text-align: center;">
                                                                <b-form-select size="sm" style="font-size: 13px;" v-model="rtitem.return_locsub" :options="packet.locsubs ? packet.locsubs.filter(function(d){ return (d.is_main_warehouse == true || d.location_id == ((packet.userDetails || {}).properties || {}).location_id ) }) : []" :disabled="rtitem.delivered == false || rtitem.delivered == null"></b-form-select>
                                                            </td>
                                                            <td style="border: 1px solid; text-align: center;">
                                                                <button @click="remove_refund_item(rtidx)">REMOVE</button>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>

                                                <div style="margin-top: 10px;">
                                                    <div style="display: inline-block; margin: 5px; vertical-align: top;">
                                                        <table style="width: 230px;">
                                                            <tr><th colspan="2" style="border: 1px solid black; text-align: center;">TOTAL REFUND ON ITEMS</th></tr>
                                                            <tr><td style="border: 1px solid black; text-align: center;">CURRENT</td>
                                                                <td style="border: 1px solid black; text-align: center;">
                                                                    <input size="sm" :value="(get_total_to_be_refunded() || 0.0).toFixed(2)" disabled="true" style="text-align: center;"></input>
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </div>
                                                    <div style="display: inline-block; margin: 5px; vertical-align: top;">
                                                        <table style="width: 230px;">
                                                            <tr><th colspan="2" style="border: 1px solid black; text-align: center;">REFUND ON DELIVERY</th></tr>
                                                            <tr><td style="border: 1px solid black; text-align: center;">REMAINING</td>
                                                                <td style="border: 1px solid black; text-align: center;">${{ (get_total_delivery_refund_remaining() || 0.0).toFixed(2) }}</td>
                                                            </tr>
                                                            <tr><td style="border: 1px solid black; text-align: center;">CURRENT</td>
                                                                <td style="border: 1px solid black; text-align: center;">
                                                                    <input size="sm" v-model="refunds.total_delivery_refund" :disabled="get_total_delivery_refund_remaining() == 0" style="text-align: center;" type="number"></input>
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </div>
                                                    <div style="display: inline-block; margin: 5px; vertical-align: top;">
                                                        <table style="width: 230px;">
                                                            <tr><th colspan="2" style="border: 1px solid black; text-align: center;">REFUND ON SETUP</th></tr>
                                                            <tr><td style="border: 1px solid black; text-align: center;">REMAINING</td>
                                                                <td style="border: 1px solid black; text-align: center;">${{ (get_total_setup_refund_remaining() || 0.0).toFixed(2) }}</td>
                                                            </tr>
                                                            <tr><td style="border: 1px solid black; text-align: center;">CURRENT</td>
                                                                <td style="border: 1px solid black; text-align: center;">
                                                                    <input size="sm" v-model="refunds.total_setup_refund" :disabled="get_total_setup_refund_remaining() == 0" style="text-align: center;" type="number"></input>
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </div>
                                                    <div style="display: inline-block; margin: 5px; vertical-align: top;">
                                                        <table style="width: 230px;">
                                                            <tr><th colspan="2" style="border: 1px solid black; background: black; color: white; text-align: center;">TOTALS</th></tr>
                                                            <tr><td style="border: 1px solid black; text-align: center;">TOTAL REFUND</td>
                                                                <td style="border: 1px solid black; text-align: center;">$ {{ (get_total_refund_dollar_value() || 0.0).toFixed(2) }}</td>
                                                            </tr>
                                                            <tr><td style="border: 1px solid black; text-align: center;">BALANCE</td>
                                                                <td style="border: 1px solid black; text-align: center; font-weight: bold; background: lightgreen;">
                                                                    $ {{ ((get_total_refund_dollar_value() || 0.0) - ( get_payment_methods_sum() || 0.0 )).toFixed(2) }}
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div style="margin-top: 10px;">
                                                    <div>
                                                        <div style="display: inline-block; vertical-align: top;">
                                                            <button @click="add_refund_payment_method">ADD REFUND PAYMENT METHOD</button>
                                                        </div>
                                                        <div style="display: inline-block; vertical-align: top;">
                                                            <b-alert style="padding: 2px; font-size: 10px;" show><sup>PLEASE ADD NON-TAXABLE FORM OF PAYMENTS FIRST</sup></b-alert>
                                                        </div>
                                                    </div>
                                                    <div v-if="refunds.refund_methods.length > 0">
                                                        <div v-for="(rpmethod, rpidx) in refunds.refund_methods" :key="rpidx" style="display: inline-block; vertical-align: top; margin: 5px; border: 1px dashed black; padding: 5px;">
                                                            <table style="width: auto; max-width: 300px;">
                                                                <tr>
                                                                    <th colspan="2" style="border: 1px solid black; text-align: center;">
                                                                        <b-form-select :options="refunds.refund_method_options" size="sm" v-model="rpmethod.value"></b-form-select>
                                                                    </th>
                                                                </tr>
                                                                <tr>
                                                                    <td style="border: 1px solid black; text-align: center;">MAX ALLOWED</td>
                                                                    <td style="border: 1px solid black; text-align: center;">
                                                                        <div v-if="rpmethod.value != null">
                                                                            ${{
                                                                                Math.max (
                                                                                (
                                                                                    get_max_allowed_on_refund_payment_method(rpmethod) || 0.0),
                                                                                    0.0
                                                                                ).toFixed(2)
                                                                            }}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td style="border: 1px solid black; text-align: center; padding: 3px;">VALUE</td>
                                                                    <td style="border: 1px solid black; text-align: center; padding: 3px;">
                                                                        <input style="text-align: center;" type="number" v-model="rpmethod.amount"></input>
                                                                    </td>
                                                                </tr>
                                                             </table>
                                                            <button style="margin-top: 5px;" @click="remove_rpm(rpidx)">REMOVE</button>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <b-button @click="submit_refund">SUBMIT REFUND</b-button>
                                                    </div>
                                                </div>
                                            </b-alert>
                                        </div>

                                        <div class="separate3" style="display: block; margin-top: 10px; width: 90%; margin-left: auto; margin-right: auto; padding-top: 50px;" v-if="invoice.refund_methods && invoice.refund_methods.length > 0">
                                            <h5><b>REFUNDS/CASH ISSUE</b></h5>
                                            <div style="border: 2px solid; margin-top: 5px; padding: 10px;">
                                                <RefundsSummary @refunds-history="summarize_refunds_history" :viewdata="{invoice_id: invoice.invoice_id, packet: packet}" />
                                            </div>
                                        </div>

                                        <!-- Terms and Conditions Section -->
                                        <div v-if="policy_text" class="separate3" style="margin-top: 15px; width: 90%; padding: 20px; margin-left: auto; margin-right: auto;">
                                            <h5><b>TERMS AND CONDITIONS / RETURN POLICY</b></h5>
                                            <div style="border: 1px solid #ddd; padding: 20px; margin-top: 10px; background-color: #f9f9f9;">
                                                <div v-html="policy_text" style="font-size: 12px; line-height: 1.5;"></div>
                                                
                                                <div style="margin-top: 20px; margin-bottom: 20px; padding: 15px; background-color: #f0f0f0; border-radius: 4px; border-left: 4px solid #007bff;">
                                                    <h6 style="font-weight: bold; margin-bottom: 15px; color: #333; font-size: 14px;">CUSTOMER AGREEMENTS:</h6>
                                                    
                                                    <div style="display: flex; align-items: flex-start; margin-bottom: 12px; padding: 8px; background-color: white; border-radius: 4px;">
                                                        <span v-if="termsAgreed" style="color: green; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✓</span>
                                                        <span v-else style="color: red; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✗</span>
                                                        <span style="font-size: 13px; line-height: 1.4;">I have read and agree to the Terms & Conditions</span>
                                                    </div>
                                                    
                                                    <div style="display: flex; align-items: flex-start; margin-bottom: 12px; padding: 8px; background-color: white; border-radius: 4px;">
                                                        <span v-if="warrantyRefused" style="color: green; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✓</span>
                                                        <span v-else style="color: red; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✗</span>
                                                        <span style="font-size: 13px; line-height: 1.4;">Customer refused to purchase extended warranties</span>
                                                    </div>
                                                    
                                                    <div style="display: flex; align-items: flex-start; margin-bottom: 12px; padding: 8px; background-color: white; border-radius: 4px;">
                                                        <span v-if="deliveryWaiverAgreed" style="color: green; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✓</span>
                                                        <span v-else style="color: red; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✗</span>
                                                        <span style="font-size: 13px; line-height: 1.4;">I understand that Standard Delivery does NOT include the removal of boxes/packaging. I choose to waive the extra $200 White Glove service.</span>
                                                    </div>
                                                    
                                                    <div style="display: flex; align-items: flex-start; margin-bottom: 12px; padding: 8px; background-color: white; border-radius: 4px;">
                                                        <span v-if="spacePreparationAgreed" style="color: green; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✓</span>
                                                        <span v-else style="color: red; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✗</span>
                                                        <span style="font-size: 13px; line-height: 1.4;">Failure to Prepare Space: If the designated room is not cleared and ready for setup upon arrival, the delivery team will perform a "Drop-Off Delivery" only. Customer has to pay $150 to reschedule setup the merchandise.</span>
                                                    </div>
                                                    
                                                    <div style="display: flex; align-items: flex-start; margin-bottom: 12px; padding: 8px; background-color: white; border-radius: 4px;">
                                                        <span v-if="addressConfirmationAgreed" style="color: green; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✓</span>
                                                        <span v-else style="color: red; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✗</span>
                                                        <span style="font-size: 13px; line-height: 1.4;">I confirm that the delivery address provided is accurate and accessible for delivery.</span>
                                                    </div>
                                                    
                                                    <div style="display: flex; align-items: flex-start; margin-bottom: 5px; padding: 8px; background-color: white; border-radius: 4px;">
                                                        <span v-if="deliveryDamageWaiverAgreed" style="color: green; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✓</span>
                                                        <span v-else style="color: red; font-size: 20px; font-weight: bold; margin-right: 12px; min-width: 24px;">✗</span>
                                                        <span style="font-size: 13px; line-height: 1.4;">I understand and accept that Furniture Mecca is not responsible for damages that occur during delivery.</span>
                                                    </div>
                                                </div>
                                                
                                                <div style="margin-top: 30px; border-top: 1px dashed #ccc; padding-top: 20px;">
                                                    <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap;">
                                                        <div style="margin-left: 20px; font-size: 15px; color: red; width:70%; font-style:italic">
                                                            <strong>Customer acknowledges that they have read the store policy and agree to all Terms and Conditions.</strong>
                                                        </div>
                                                        <div style="display: flex; justify-content: space-between; align-items: center; width:20%">
                                                            <div>
                                                                <strong>CUSTOMER SIGNATURE:</strong>
                                                            </div>
                                                            <div v-if="invoice.signature_url" style="text-align: right;">
                                                                <img 
                                                                    :src="getSignatureUrl(invoice.signature_url)" 
                                                                    alt="Customer Signature"
                                                                    style="max-height: 60px; max-width: 250px; border: 1px solid #eee; padding: 5px;"
                                                                    @error="handleSignatureError"
                                                                />
                                                            </div>
                                                            <div v-else style="border-bottom: 2px solid #000; min-width: 200px; height: 30px;">
                                                                &nbsp;
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Print version of policy -->
                                        <div class="print-only-policy" style="display: none;">
                                            <div style="page-break-before: always;">
                                                <div style="text-align: center">
                                                    <h3 style="text-decoration: underline; font-size: 18px;"><b>TERMS AND CONDITIONS / RETURN POLICY</b></h3>
                                                </div>
                                                <div v-html="policy_text" style="margin-left: 40px; font-size: 8px; line-height: 1.1; width: 90%;"></div>
                                                
                                                <div style="margin-left: 40px; margin-top: 0px; margin-bottom: 0px; width: 90%; padding-top: 0px;">
                                                    <h6 style="font-size: 14px; font-weight: bold; margin-bottom: 15px;">CUSTOMER AGREEMENTS:</h6>
                                                    
                                                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
                                                        <div style="display: flex; align-items: flex-start;">
                                                            <span v-if="termsAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                            <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                            <span style="font-size: 11px;">I have read and agree to the Terms & Conditions</span>
                                                        </div>
                                                        <div style="display: flex; align-items: flex-start;">
                                                            <span v-if="warrantyRefused" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                            <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                            <span style="font-size: 11px;">Customer refused to purchase extended warranties</span>
                                                        </div>
                                                        <div style="display: flex; align-items: flex-start;">
                                                            <span v-if="deliveryWaiverAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                            <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                            <span style="font-size: 11px;">Waived White Glove service (Standard Delivery only)</span>
                                                        </div>
                                                        <div style="display: flex; align-items: flex-start;">
                                                            <span v-if="spacePreparationAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                            <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                            <span style="font-size: 11px;">Acknowledged space preparation requirement ($150 reschedule fee)</span>
                                                        </div>
                                                        <div style="display: flex; align-items: flex-start;">
                                                            <span v-if="addressConfirmationAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                            <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                            <span style="font-size: 11px;">Confirmed delivery address accuracy and accessibility</span>
                                                        </div>
                                                        <div style="display: flex; align-items: flex-start;">
                                                            <span v-if="deliveryDamageWaiverAgreed" style="color: green; font-size: 16px; font-weight: bold; margin-right: 10px;">✓</span>
                                                            <span v-else style="color: red; font-size: 16px; font-weight: bold; margin-right: 10px;">✗</span>
                                                            <span style="font-size: 11px;">Accepted delivery damage waiver (FM not responsible for delivery damages)</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div style="text-align: right; width: 100%; margin-top: 20px;">
                                                    <div v-if="invoice.signature_url" style="text-align: right;">
                                                        <img 
                                                            :src="getSignatureUrl(invoice.signature_url)" 
                                                            alt="Customer Signature"
                                                            style="max-height: 80px; max-width: 300px;"
                                                            @error="handleSignatureError"
                                                        />
                                                    </div>
                                                    <div v-else>
                                                        _____________________________________
                                                    </div>
                                                    <div style="margin-bottom: 10px; font-size: 12px;">
                                                        <strong style="color:red;font-style:italic">Customer acknowledges that they have read the store policy and agree to all terms and conditions.</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
    import moment from 'moment'
    import mappings from '@/utilities/mappings'
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    import Requestor from '@/utilities/Requestor'

    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    import CustomerServiceTicket from '@/components/CustomerService/CustomerServiceTicket'
    import RefundsSummary from "@/components/importables/RefundsSummary"
    import LayAwayPaymentsModal from "@/components/Sales/LayAwayPaymentsModal"
    import EditInvoiceWrapper from '@/components/Sales/EditInvoiceWrapper'
    import TicketListModal from '@/components/CustomerService/TicketListModal';

    import DatePicker from 'vue2-datepicker';
    import 'vue2-datepicker/index.css';

    export default {
        name: "ViewSale2",
        props: ["viewdata"],
        components: {flatPickr, Loading, CustomerServiceTicket, RefundsSummary, DatePicker, EditInvoiceWrapper, LayAwayPaymentsModal,  TicketListModal},
        data: function() {
            return {
                route: null,
                now: this.get_time(),
                packet: {},
                currentSignatureUrl: null,
                invoice: {
                    customer: {
                        fullname: '',
                        address_line_1: '',
                        address_line_2: '',
                        phone: '',
                        phone_2: '',
                        email: '',
                        city: '',
                        state: '',
                        zipcode: '',
                        floor: ''
                    }
                },
                order_types_dict: {
                    "store_pickup": "STORE PICKUP",
                    "home_delivery": "HOME DELIVERY",
                    "customer_takes": "CUSTOMER TOOK"
                },
                all_loc_inventory_dict: {},
                financeLeaseId: null,
                isPageLoading: true,
                loadingProgress: 0,
                loadingInterval: null,
                financeMiscInfo: null,
                showFinanceDetails: false,
                flatpickrConfig: { dateFormat: "Y-m-d" },
                states: mappings.states,
                currentTicketItem: null,
                currentTickets: [],
                merchandise_statuses: [
                    {"value": null, "text": "NONE"},
                    {"value": "IN STOCK", "text": "IN STOCK"},
                    {"value": "SPECIAL", "text": "SPECIAL"},
                    {"value": "BACKORDER", "text": "BACKORDER"},
                    {"value": "ORDERED", "text": "ORDERED"},
                    {"value": "DISCONTINUED", "text": "DISCONTINUED"},
                    {"value": "COMPLETED", "text": "COMPLETED"},
                ],
                update_disabled: false,
                deliveryWaiverAgreed: false,
                spacePreparationAgreed: false,
                ticketsLoading: false,          
                deliveryDamageWaiverAgreed: false,
                addressConfirmationAgreed: false,
                termsAgreed: false,
                warrantyRefused: false,
                invoice_location_details: {},
                processed_statuses: [
                    {"value": null, "text": "NEW ORDER"},
                    {"value": "open", "text": "OPEN ORDER"},
                    {"value": "processed", "text": "PROCESSED"},
                ],
                processed_status: null,
                refund_payment_modes_dict: {
                    1: "CC/CASH RCVD",
                    2: "STORE PURCHASE"
                },
                refunded_total: null,
                invoice_being_loaded: false,
                cs_item_data: {},
                return_reasons: [
                    {value: 0, text: "OTHER"},
                    {value: 1, text: "ITEM DAMAGED"},
                    {value: 2, text: "ITEM INCOMPLETE"},
                ],
                refunds: this.get_refunds_packet(),
                is_delivered: [
                    {value: false, text: "NO"},
                    {value: true, text: "YES"}
                ],
                refund_perc_vals: [
                    {value: 0.0, text: "0%"},
                    {value: 0.10, text: "10%"},
                    {value: 0.20, text: "20%"},
                    {value: 0.30, text: "30%"},
                    {value: 0.40, text: "40%"},
                    {value: 0.50, text: "50%"},
                    {value: 0.60, text: "60%"},
                    {value: 0.70, text: "70%"},
                    {value: 0.80, text: "80%"},
                    {value: 0.90, text: "90%"},
                    {value: 1.0, text: "100%"},
                ],
                policy_text: null,
                edit_invoice_action: false,
                total_refunded_value: 0.0,
                order_types: [
                    {"value": "customer_takes", "text": "Take Home"},
                    {"value": "store_pickup", "text": "Store Pickup"},
                    {"value": "home_delivery", "text": "Home Delivery"},
                    {"value": "delivery_from_store", "text": "Delivery from Store"},
                ],
                finance_companies: [],
                currentFullSizeImage: null,
                currentProductName: null,
                // Parent invoice data for exchange invoices
                parentInvoiceData: null,
                parentInvoicePaymentMethods: [],
                showParentPaymentInfo: false,
            }
        },
        computed: {
            hasChildInvoices() {
                return this.invoice.child_invoices && this.invoice.child_invoices.length > 0;
            },
            childInvoiceCount() {
                return this.invoice.child_invoices ? this.invoice.child_invoices.length : 0;
            },
            totalRefundAmount() {
                return this.total_refunded_value || 0;
            },
            hasTaxExemptPayment() {
                if (!this.invoice.payment_methods) return false;
                return this.invoice.payment_methods.some(pm => pm.value === 'tax_exempt');
            },
            getTaxExemptCompanyNames() {
                if (!this.invoice.payment_methods) return [];
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'tax_exempt' && pm.name)
                    .map(pm => pm.name);
            },
            getTaxExemptTotal() {
                if (!this.invoice.payment_methods) return 0;
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'tax_exempt')
                    .reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
            },
            getTaxExemptOrFinanceTotal() {
                if (this.hasTaxExemptPayment) {
                    return this.getTaxExemptTotal;
                }
                return this.getFinanceTotal;
            },
            allFinanceAgreements() {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return [];
                }
                
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'finance')
                    .map(pm => {
                        const financeCompany = this.finance_companies.find(fc => fc.value === pm.name);
                        
                        return {
                            company_name: pm.name,
                            company_ein: pm.ein || null,
                            amount: parseFloat(pm.amount) || 0,
                            lease_id: pm.lease_id || null,
                            misc_info: pm.misc_info || null,
                            company_phone: financeCompany ? financeCompany.phone_number : null,
                            charged_tax_itself: pm.charged_tax_itself || false,
                            idx: pm.idx || 0,
                            ts: pm.ts || null
                        };
                    });
            },
            allTaxExemptAgreements() {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return [];
                }
                
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'tax_exempt')
                    .map(pm => ({
                        company_ein: pm.ein || null,
                        company_name: pm.name,
                        amount: parseFloat(pm.amount) || 0,
                        idx: pm.idx || 0,
                        ts: pm.ts || null
                    }));
            },
            financeLeaseInfo() {
                return this.allFinanceAgreements[0] || { 
                    lease_id: null, 
                    misc_info: null, 
                    company_name: null, 
                    company_phone: null,
                    amount: 0
                };
            },
            hasFinanceLeaseInfo() {
                return this.allFinanceAgreements.length > 0;
            },
            getFinanceTotal() {
                if (!this.invoice.payment_methods) return 0;
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'finance')
                    .reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
            },
            getFinanceCompanyNames() {
                if (!this.invoice.payment_methods) return [];
                return this.invoice.payment_methods
                    .filter(pm => pm.value === 'finance' && pm.name)
                    .map(pm => pm.name);
            },
            predictedSalesTax() {
                const remainingBalance = this.get_total_balance();
                if (remainingBalance <= 0) return 0;
                
                const taxRate = parseFloat(this.invoice.tax_perc) || 0;
                if (taxRate === 0) return 0;
                
                const soldFromState = this.invoice.sold_from_state || 'PA';
                let taxableRemaining = remainingBalance;
                
                if (this.invoice.payment_methods && this.invoice.payment_methods.length > 0) {
                    const lastPayment = this.invoice.payment_methods[this.invoice.payment_methods.length - 1];
                    if (lastPayment && lastPayment.value === 'finance') {
                        const financeCompany = this.finance_companies.find(fc => fc.value === lastPayment.name);
                        if (financeCompany && 
                            financeCompany.company_charges_tax_in_states && 
                            !financeCompany.company_charges_tax_in_states.includes(soldFromState)) {
                            taxableRemaining = 0;
                        }
                    }
                }
                
                return this.roundTo(taxableRemaining * taxRate, 2);
            },
            totalWithPredictedTax() {
                const remainingBalance = this.get_total_balance();
                const predictedTax = this.predictedSalesTax;
                
                return {
                    remainingBalance: remainingBalance,
                    predictedTax: predictedTax,
                    totalToPay: remainingBalance + predictedTax
                };
            },
            groupedByParent() {
                const groups = {};
                if (!this.invoice.items) return groups;
                this.invoice.items.forEach(item => {
                    const parentSku = item.parent_product_sku || item.product_sku;
                    if (!groups[parentSku]) {
                        groups[parentSku] = [];
                    }
                    groups[parentSku].push(item);
                });
                return groups;
            },
            orderedParentSkuList() {
                const groups = this.groupedByParent;
                const individualSkus = [];
                const groupSkus = [];
                
                Object.keys(groups).forEach(parentSku => {
                    const group = groups[parentSku];
                    if (group[0].parent_product_sku && group.length > 1) {
                        groupSkus.push(parentSku);
                    } else {
                        individualSkus.push(parentSku);
                    }
                });
                
                return [...individualSkus, ...groupSkus];
            },
            parentSkuOrder() {
                return Object.keys(this.groupedByParent);
            },
            // Check if this is an exchange invoice
            isExchangeInvoice() {
                return this.invoice.invoice_type === 'EX' && this.invoice.parent_invoice_id;
            },
            // Check if we should show parent payment info
            shouldShowParentPayment() {
                const isExchange = this.invoice.invoice_type === 'EX' && this.invoice.parent_invoice_id;
                
                let paymentMethods = this.parentInvoicePaymentMethods;
                
                if ((!paymentMethods || paymentMethods.length === 0) && this.parentInvoiceData) {
                    paymentMethods = this.parentInvoiceData.payment_methods || [];
                    this.parentInvoicePaymentMethods = paymentMethods;
                }
                
                return isExchange && this.parentInvoiceData && paymentMethods && paymentMethods.length > 0;
            },
            // Get parent invoice total
            parentInvoiceTotal() {
                if (!this.parentInvoiceData) return 0;
                return parseFloat(this.parentInvoiceData.total) || 0;
            },
            // Get parent payment methods with allocated amounts for exchange items
            getParentPaymentMethodsForItems() {
                if (!this.shouldShowParentPayment) return [];
                
                const methods = this.parentInvoicePaymentMethods;
                const exchangeTotal = this.getExchangeTotal;
                
                if (exchangeTotal === 0 || methods.length === 0) return [];
                
                const parentTotal = methods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
                if (parentTotal === 0) return methods;
                
                const percentage = Math.min(exchangeTotal / parentTotal, 1);
                
                return methods.map(pm => ({
                    ...pm,
                    allocated_amount: (parseFloat(pm.amount) || 0) * percentage,
                    display_amount: ((parseFloat(pm.amount) || 0) * percentage).toFixed(2),
                    original_amount: pm.amount,
                }));
            },
            // Get parent payment total
            parentPaymentTotal() {
                if (!this.parentInvoicePaymentMethods) return 0;
                return this.parentInvoicePaymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
            },
            // Get allocated parent payment total for exchange items
            allocatedParentPaymentTotal() {
                const methods = this.getParentPaymentMethodsForItems;
                if (!methods || methods.length === 0) return 0;
                return methods.reduce((sum, pm) => sum + (parseFloat(pm.allocated_amount) || 0), 0);
            },
            // Get exchange total
            getExchangeTotal() {
                if (!this.invoice.items) return 0;
                return this.invoice.items.reduce((sum, item) => {
                    const quantity = (item.quantity || 0) - (item.refunded_so_far || 0);
                    return sum + ((item.selling_price || 0) * quantity) - (item.discount_offered || 0);
                }, 0);
            },
        },
        methods: {
            // NEW: Get spiff value from item
            getSpiffValue(item) {
                if (!item) return 0;
                
                // Check if spiff is directly available on the item (from backend)
                if (item.spiff !== undefined && item.spiff !== null) {
                    return parseFloat(item.spiff) || 0;
                }
                
                // Check if spiff is in properties
                if (item.properties && item.properties.spiff !== undefined) {
                    return parseFloat(item.properties.spiff) || 0;
                }
                
                return 0;
            },
            // Helper methods for safe customer data access
            getCustomerFullName() {
                if (!this.invoice || !this.invoice.customer) return 'N/A';
                return this.invoice.customer.fullname || 'N/A';
            },
            getCustomerAddress() {
                if (!this.invoice || !this.invoice.customer) return '';
                return `${this.invoice.customer.address_line_1 || ''} ${this.invoice.customer.address_line_2 || ''}`.trim();
            },
            getCustomerCityStateZip() {
                if (!this.invoice || !this.invoice.customer) return '';
                const parts = [
                    this.invoice.customer.city || '',
                    this.invoice.customer.state || '',
                    this.invoice.customer.zipcode || ''
                ];
                return parts.filter(p => p).join(', ');
            },
            getCustomerPhone() {
                if (!this.invoice || !this.invoice.customer) return '';
                return this.invoice.customer.phone || '';
            },
            getCustomerPhone2() {
                if (!this.invoice || !this.invoice.customer) return '';
                return this.invoice.customer.phone_2 || '';
            },
            // NEW: View parent invoice in new tab with clean URL
            viewParentInvoice() {
                if (!this.invoice.parent_invoice_id) {
                    this.$bvToast.toast("No parent invoice ID found", {
                        title: "Error", 
                        autoHideDelay: 1500, 
                        variant: "danger"
                    });
                    return;
                }
                
                const basePath = '/sales/viewsale';
                const newPath = `${basePath}/${this.invoice.parent_invoice_id}`;
                
                const route = this.$router.resolve(newPath);
                window.open(route.href, '_blank');
                
                this.$bvToast.toast(`Opening parent invoice: ${this.invoice.parent_invoice_id} in new tab`, {
                    title: "Parent Invoice",
                    autoHideDelay: 2000,
                    variant: "info"
                });
            },
            // MODIFIED: Open child invoice in new tab with clean URL
            viewChildInvoice(childInvoiceId) {
                const basePath = '/sales/viewsale';
                const newPath = `${basePath}/${childInvoiceId}`;
                
                const route = this.$router.resolve(newPath);
                window.open(route.href, '_blank');
                
                this.$bvToast.toast(`Opening child invoice: ${childInvoiceId} in new tab`, {
                    title: "Child Invoice",
                    autoHideDelay: 2000,
                    variant: "info"
                });
            },
            reloadInvoiceById(invoiceId) {
                let that = this;
                let path = "/api/sales/get_invoice/";
                let data = { invoice_id: invoiceId };
                
                let s = function(response) {
                    if (response.data.result) {
                        that.invoice = response.data.result;
                        that.get_invoice_location_address();
                        that.fetchTicketsForInvoice();
                        that.$forceUpdate();
                    }
                };
                
                let f = function(error) {
                    that.$bvToast.toast("Error loading child invoice", {
                        title: "Error", 
                        autoHideDelay: 1500, 
                        variant: "danger"
                    });
                };
                
                Requestor.postAsyncP(path, data, this, s, f);
            },
            handleDeliveryFromStore(item) {
                if (item.order_type === 'delivery_from_store') {
                    if (!item.drop_locsub || item.drop_locsub === 'null' || item.drop_locsub === null) {
                        const storeLocationId = this.invoice.location_id || 25;
                        const deliveryLocation = this.packet.locsubs ? this.packet.locsubs.find(loc => 
                            loc.location_id === storeLocationId && 
                            loc.text.toLowerCase().includes('customer deliveries')
                        ) : null;
                        
                        if (deliveryLocation) {
                            item.drop_locsub = deliveryLocation.value;
                            console.log(`Set drop_locsub for ${item.product_sku}: ${item.drop_locsub}`);
                        } else {
                            if (this.packet.default_locsubs && this.packet.default_locsubs.length > 0) {
                                const defaultLoc = this.packet.default_locsubs.find(d => 
                                    d.order_type === 'delivery_from_store' || d.order_type === 'home_delivery'
                                );
                                if (defaultLoc) {
                                    item.drop_locsub = defaultLoc.locsub;
                                }
                            }
                        }
                    }
                    
                    if (!item.locsub || item.locsub === 'null' || item.locsub === null) {
                        const storeLocations = this.packet.locsubs ? this.packet.locsubs.filter(loc => 
                            loc.location_id === this.invoice.location_id && 
                            loc.sublocation_type_id !== 1
                        ) : [];
                        
                        if (storeLocations.length > 0) {
                            const stockLoc = storeLocations.find(loc => 
                                loc.text.toLowerCase().includes('stock')
                            );
                            item.locsub = stockLoc ? stockLoc.value : storeLocations[0].value;
                            console.log(`Set locsub for ${item.product_sku}: ${item.locsub}`);
                        }
                    }
                }
            },
            getSignatureUrl(url) {
                if (!url) return '';
                const imageUrl = url.trim();
                if (imageUrl && imageUrl.includes('blob.core.windows.net')) {
                    return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                }
                return imageUrl;
            },
            handleSignatureError(event) {
                console.warn('Signature image failed to load:', event.target.src);
                const parent = event.target.parentElement;
                if (parent) {
                    parent.innerHTML = '<span style="border-bottom: 2px solid #000; min-height: 40px; display: block; margin-top: 5px;">&nbsp;</span>';
                }
            },
            startLoadingAnimation() {
                this.isPageLoading = true;
                this.loadingProgress = 0;
                if (this.loadingInterval) clearInterval(this.loadingInterval);
                this.loadingInterval = setInterval(() => {
                    if (this.loadingProgress < 90) {
                        this.loadingProgress += Math.random() * 10;
                        if (this.loadingProgress > 90) this.loadingProgress = 90;
                    }
                }, 200);
            },
            stopLoadingAnimation() {
                if (this.loadingInterval) {
                    clearInterval(this.loadingInterval);
                    this.loadingInterval = null;
                }
                this.loadingProgress = 100;
                setTimeout(() => {
                    this.isPageLoading = false;
                }, 300);
            },
            getTotalRefundAmount() {
                const totalRefunded = this.total_refunded_value || 0;
                const orderTotal = this.getOrderTotal();
                const chargesTotal = this.getChargesTotal();
                const totalInvoiceAmount = orderTotal + chargesTotal;
                
                if (totalInvoiceAmount === 0) {
                    return 0;
                }
                
                return Math.min(totalRefunded, totalInvoiceAmount);
            },
            getRefundTaxAmount() {
                const totalRefunded = this.getTotalRefundAmount() || 0;
                const taxRate = parseFloat(this.invoice.tax_perc) || 0;
                return Math.round((totalRefunded * taxRate) * 100) / 100;
            },
            getOrderTotal() {
                let itemsTotal = 0;
                if (this.invoice.items && this.invoice.items.length > 0) {
                    for (let i = 0; i < this.invoice.items.length; i++) {
                        const item = this.invoice.items[i];
                        const quantity = (item.quantity || 0) - (item.refunded_so_far || 0);
                        const itemTotal = ((item.selling_price || 0) * quantity) - (item.discount_offered || 0);
                        itemsTotal += itemTotal;
                    }
                }
                return Math.max(0, Math.round(itemsTotal * 100) / 100);
            },
            // NEW: Calculate total discount from all items
            getTotalDiscount() {
                let totalDiscount = 0;
                if (this.invoice.items && this.invoice.items.length > 0) {
                    for (let i = 0; i < this.invoice.items.length; i++) {
                        const item = this.invoice.items[i];
                        // ONLY use ONE of these, not both
                        // Use unit_discount_value (this is the per-unit discount)
                        totalDiscount += (item.unit_discount_value || 0) * (item.quantity || 0);
                        
                        // OR use discount_offered (this is the total discount for the item)
                        // totalDiscount += (item.discount_offered || 0);
                        
                        // BUT NOT BOTH!
                    }
                }
                return Math.round(totalDiscount * 100) / 100;
            },
            getChargesTotal() {
                let delivery = parseFloat(this.invoice.delivery_charges) || 0.0;
                let setup = parseFloat(this.invoice.setup_charges) || 0.0;
                let warranty = parseFloat(this.invoice.warranty_charges) || 0.0;
                return (delivery + setup + warranty);
            },
            getTaxableSubtotal() {
    const orderTotal = this.getOrderTotal();
    const chargesTotal = this.getChargesTotal();
    
    if (orderTotal === 0 && chargesTotal === 0) {
        return 0;
    }
    
    return Math.max(0, Math.round((orderTotal + chargesTotal) * 100) / 100);
},
            getSalesTaxAmount() {
                const salesTax = parseFloat(this.invoice.sales_tax) || 0;
                const refundTax = this.getRefundTaxAmount() || 0;
                const taxableSubtotal = this.getTaxableSubtotal();
                
                if (taxableSubtotal === 0) {
                    return 0;
                }
                
                const taxAmount = Math.max(0, salesTax - refundTax);
                return Math.round(taxAmount * 100) / 100;
            },
            getGrandTotal() {
                const orderTotal = this.getOrderTotal();
                const chargesTotal = this.getChargesTotal();
                const salesTax = this.getSalesTaxAmount();
                const refundAmount = this.getTotalRefundAmount();
                const refundTax = this.getRefundTaxAmount();
                
                if (orderTotal === 0 && chargesTotal === 0) {
                    return 0;
                }
                
                const originalTotal = parseFloat(this.invoice.total) || 0;
                const grandTotal = originalTotal - refundAmount - refundTax;
                return Math.max(0, Math.round(grandTotal * 100) / 100);
            },
          getSubTotal() {
    // Calculate from items, excluding refunded quantities
    let subtotal = 0;
    if (this.invoice.items && this.invoice.items.length > 0) {
        for (let i = 0; i < this.invoice.items.length; i++) {
            const item = this.invoice.items[i];
            // Get remaining quantity (original quantity minus refunded quantity)
            const remainingQty = (item.quantity || 0) - (item.refunded_so_far || 0);
            // Only include items that still have quantity remaining
            if (remainingQty > 0) {
                // Calculate item subtotal: (selling_price * remaining_qty) - discount
                const unitPrice = parseFloat(item.selling_price) || 0;
                const unitDiscount = parseFloat(item.unit_discount_value) || 0;
                const discountOffered = parseFloat(item.discount_offered) || 0;
                
                // Use discount_offered if it exists (total discount for the item)
                // Otherwise calculate from unit_discount_value
                let itemTotal = 0;
                if (discountOffered > 0) {
                    // If discount_offered is the total discount for the item
                    // We need to prorate it based on remaining quantity
                    const originalQty = parseFloat(item.quantity) || 1;
                    const proratedDiscount = discountOffered * (remainingQty / originalQty);
                    itemTotal = (unitPrice * remainingQty) - proratedDiscount;
                } else {
                    // Use unit_discount_value
                    itemTotal = (unitPrice * remainingQty) - (unitDiscount * remainingQty);
                }
                subtotal += itemTotal;
            }
        }
    }
    return Math.round(subtotal * 100) / 100;
},
            get_total_balance() {
                const orderTotal = this.getOrderTotal();
                const chargesTotal = this.getChargesTotal();
                const totalPaid = this.get_pms_total();
                const totalRefunded = this.total_refunded_value || 0.0;
                
                if (orderTotal === 0 && chargesTotal === 0) {
                    return 0;
                }
                
                let balance = (orderTotal + chargesTotal) - totalPaid - totalRefunded;
                
                if (balance < 0 && orderTotal === 0) {
                    balance = 0;
                }
                
                return Math.max(0, Math.round(balance * 100) / 100);
            },
            get_pms_total() {
                let total = 0.0;
                if (this.invoice.payment_methods && this.invoice.payment_methods.length > 0) {
                    for (let i = 0; i < this.invoice.payment_methods.length; i++) {
                        total += parseFloat(this.invoice.payment_methods[i].amount) || 0.0;
                    }
                }
                return Math.round(total * 100) / 100;
            },
            onOrderTypeChange(item, newValue, oldValue) {
                const previousOrderType = oldValue;
                item.order_type = newValue;
                
                if (newValue === 'delivery_from_store') {
                    this.handleDeliveryFromStore(item);
                }
                
                this.update_item_level_with_history(item, previousOrderType);
            },
            update_item_level_with_history(item, previousOrderType) {
                let that = this;
                
                let data = {
                    invoice_id: item.invoice_id,
                    itemid: item.itemid,
                    product_sku: item.product_sku,
                    delivery_date: item.delivery_date,
                    order_type: item.order_type,
                    previous_order_type: previousOrderType,
                    pull_date: moment(item.delivery_date).add(-1, 'day').format("MM-DD-YYYY"),
                    username: this.packet.userDetails ? this.packet.userDetails.username : ''
                };
                
                let path = "/api/sales/update_item_level/";
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("UPDATED ITEM DATA.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                        
                        if (response.data.order_type_history) {
                            item.order_type_history = response.data.order_type_history;
                        }
                    }
                };
                let f = function(error) {
                    that.$bvToast.toast("Error updating delivery date. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },
            getOrderTypeHistory(item) {
                if (!item.order_type_history || item.order_type_history.length === 0) {
                    return [];
                }
                return item.order_type_history;
            },
            formatOrderTypeHistory(item) {
                const history = this.getOrderTypeHistory(item);
                if (history.length === 0) return '';
                
                return history.map(change => {
                    const oldType = change.previous_order_type || 'Not set';
                    const newType = change.order_type;
                    const changedBy = change.changed_by;
                    const changedAt = moment(change.changed_at).format('MM/DD/YYYY hh:mm A');
                    
                    return `${oldType} → ${newType} (by ${changedBy} on ${changedAt})`;
                }).join('; ');
            },
            getOrderTypeHistoryTooltip(item) {
                const history = this.getOrderTypeHistory(item);
                if (history.length === 0) return 'No order type changes';
                
                return history.map(change => {
                    const oldType = change.previous_order_type || 'Not set';
                    const newType = change.order_type;
                    const changedBy = change.changed_by;
                    const changedAt = moment(change.changed_at).format('MM/DD/YYYY hh:mm A');
                    
                    return `Changed from "${oldType}" to "${newType}" by ${changedBy} on ${changedAt}`;
                }).join('\n');
            },
            formatDate(timestamp) {
                if (!timestamp) return '';
                try {
                    return moment(timestamp).format('MM/DD/YYYY hh:mm A');
                } catch(e) {
                    return timestamp;
                }
            },
            getDayOfWeek(dateString) {
                if (!dateString) return '';
                const [year, month, day] = dateString.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('en-US', { weekday: 'long' });
            },
            handleViewTicket(ticket) {
                this.cs_item_data = { 
                    id: ticket.id, 
                    invoice_id: ticket.invoice_id, 
                    product_sku: ticket.product_sku, 
                    itemid: ticket.itemid 
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            handleCreateTicket(item) {
                this.cs_item_data = { 
                    id: null,
                    invoice_id: item.invoice_id, 
                    product_sku: item.product_sku, 
                    itemid: item.itemid,
                    is_new: true
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            hasTicket(item) {
                if (item.cs_ticket_id) {
                    return true;
                }
                if (item.cs_ticket_count && item.cs_ticket_count > 0) {
                    return true;
                }
                if (item.tickets && item.tickets.length > 0) {
                    return true;
                }
                if (item.ticket_id) {
                    return true;
                }
                return false;
            },
            fetchTicketsForInvoice() {
                let that = this;
                let path = "/api/customer_service/get_tickets_by_invoice/";
                let data = { invoice_id: this.invoice.invoice_id };
                
                let s = function(response) {
                    if (response.data && response.data.result) {
                        const ticketsByKey = response.data.result;
                        
                        if (!that.invoice.items || that.invoice.items.length === 0) {
                            return;
                        }
                        
                        that.invoice.items.forEach((item, index) => {
                            const exactKey = `${item.product_sku}_${item.itemid}`;
                            let tickets = ticketsByKey[exactKey] || [];
                            
                            if (tickets.length === 0) {
                                const fallbackKey = `${item.product_sku}_-1`;
                                tickets = ticketsByKey[fallbackKey] || [];
                            }
                            
                            if (tickets.length > 0) {
                                item.tickets = tickets;
                                item.cs_ticket_count = tickets.length;
                                
                                const sortedTickets = [...tickets].sort((a, b) => {
                                    return new Date(b.created_on) - new Date(a.created_on);
                                });
                                
                                item.cs_ticket_id = sortedTickets[0].id;
                                item.cs_ticket_status = sortedTickets[0].status;
                                item.cs_ticket_action = sortedTickets[0].action;
                            } else {
                                item.tickets = [];
                                item.cs_ticket_count = 0;
                                item.cs_ticket_id = null;
                            }
                        });
                        
                        that.$forceUpdate();
                    }
                };
                
                let f = function(error) {
                    console.error('Error fetching tickets:', error);
                };
                
                Requestor.getAsyncP(path, data, this, s, f);
            },
            view_cs_issue(item) {
                if (item.tickets && item.tickets.length > 0) {
                    if (item.tickets.length > 1) {
                        this.currentTicketItem = item;
                        this.currentTickets = item.tickets;
                        this.$bvModal.show("modal-ticket-list");
                        return;
                    }
                    
                    const ticket = item.tickets[0];
                    this.cs_item_data = { 
                        id: ticket.id, 
                        invoice_id: item.invoice_id, 
                        product_sku: item.product_sku, 
                        itemid: item.itemid 
                    };
                    this.$bvModal.show("modal-cs-ticket");
                    return;
                }
                
                if (item.cs_ticket_id) {
                    this.cs_item_data = { 
                        id: item.cs_ticket_id, 
                        invoice_id: item.invoice_id, 
                        product_sku: item.product_sku, 
                        itemid: item.itemid 
                    };
                    this.$bvModal.show("modal-cs-ticket");
                    return;
                }
                
                this.$bvModal.msgBoxConfirm(
                    "No claim exists for this item. Would you like to create one?",
                    {
                        title: "Create Claim",
                        okVariant: "primary",
                        okTitle: "Yes, Create Claim",
                        cancelTitle: "Cancel",
                        centered: true
                    }
                ).then(value => {
                    if (value) {
                        this.createNewTicketForItem(item);
                    }
                });
            },
            createNewTicketForItem(item) {
                this.cs_item_data = { 
                    id: null,
                    invoice_id: item.invoice_id, 
                    product_sku: item.product_sku, 
                    itemid: item.itemid,
                    is_new: true
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            roundTo(n, digits) {
                var negative = false;
                if (digits === undefined) {
                    digits = 0;
                }
                if (n < 0) {
                    negative = true;
                    n = n * -1;
                }
                var multiplicator = Math.pow(10, digits);
                n = parseFloat((n * multiplicator).toFixed(11));
                n = (Math.round(n) / multiplicator).toFixed(digits);
                if (negative) {
                    n = (n * -1).toFixed(digits);
                }
                return parseFloat(n);
            },
            parentImageForGroup(parentSku) {
                const group = this.groupedByParent[parentSku];
                if (group && group.length > 0 && group[0].parent_image_url) {
                    const imageUrl = group[0].parent_image_url.trim();
                    if (imageUrl && imageUrl.includes('fmapi.myfurnituremecca.com')) {
                        return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                    }
                    return imageUrl;
                }
                return null;
            },
            calculateParentUnitPrice(items) {
                if (!items || items.length === 0) return '0.00';
                const totalUnitPrice = items.reduce((sum, item) => {
                    const unitPrice = parseFloat(item.selling_price) || 0;
                    const qty = parseFloat(item.quantity) || 0;
                    return sum + (unitPrice * qty);
                }, 0);
                return totalUnitPrice.toFixed(2);
            },
            calculateParentTotalDiscount(items) {
                if (!items || items.length === 0) return '0.00';
                const totalDiscount = items.reduce((sum, item) => {
                    // Calculate discount for this child item
                    const unitDiscount = parseFloat(item.unit_discount_value) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    const itemDiscount = parseFloat(item.discount_offered) || 0;
                    
                    // Use discount_offered if available, otherwise calculate from unit_discount_value
                    // Avoid double counting!
                    if (itemDiscount > 0) {
                        return sum + itemDiscount;
                    } else {
                        return sum + (unitDiscount * quantity);
                    }
                }, 0);
                return totalDiscount.toFixed(2);
            },
            calculateParentTotalAfterDiscount(items) {
                if (!items || items.length === 0) return '0.00';
                const total = items.reduce((sum, item) => {
                    const price = parseFloat(item.selling_price) || 0;
                    const qty = parseFloat(item.quantity) || 0;
                    const discount = parseFloat(item.discount_offered) || 0;
                    return sum + (price * qty) - discount;
                }, 0);
                return total.toFixed(2);
            },
            calculateParentCubes(items) {
                if (!items || items.length === 0) return '0.00';
                const total = items.reduce((sum, item) => {
                    return sum + ((item.cubes || 0) * (item.quantity || 0));
                }, 0);
                return parseFloat(total).toFixed(2);
            },
            calculateParentTotal(items) {
                if (!items || items.length === 0) return '0.00';
                const total = items.reduce((sum, item) => {
                    return sum + ((item.selling_price || 0) * (item.quantity || 0) - (item.discount_offered || 0));
                }, 0);
                return parseFloat(total).toFixed(2);
            },
            formatOrderType(type) {
                const types = {
                    'customer_takes': 'Take Home',
                    'store_pickup': 'Store Pickup',
                    'home_delivery': 'Home Delivery',
                    'delivery_from_store': 'Delivery from Store'
                };
                return types[type] || type || '';
            },
            getStateDisplayName(stateCode) {
                if (!stateCode && stateCode !== 0) return '';
                const code = String(stateCode);
                const state = this.states.find(s => String(s.value) === code);
                return state ? state.text : code;
            },
            getLocationDisplay(locsubKey) {
                if (!locsubKey) return null;
                const cleanKey = locsubKey.toString().trim();
                const displayName = this.packet.locsubsdict ? this.packet.locsubsdict[cleanKey] : null;
                if (displayName) return displayName;
                if (cleanKey.includes('|')) {
                    const [locationId, sublocationId] = cleanKey.split('|');
                    const location = this.packet.locations ? this.packet.locations.find(loc => loc.id === parseInt(locationId)) : null;
                    if (location) {
                        return `${location.name || location.description} (${sublocationId})`;
                    }
                    return `Location ${locationId}-${sublocationId}`;
                }
                return cleanKey;
            },
            printReceipt() {
                const printFrame = document.createElement('iframe');
                printFrame.style.position = 'absolute';
                printFrame.style.width = '0';
                printFrame.style.height = '0';
                printFrame.style.border = 'none';
                document.body.appendChild(printFrame);
                
                const receiptContent = this.generateReceiptHTML();
                const frameDoc = printFrame.contentWindow.document;
                frameDoc.open();
                frameDoc.write(receiptContent);
                frameDoc.close();
                
                printFrame.onload = () => {
                    setTimeout(() => {
                        printFrame.contentWindow.focus();
                        printFrame.contentWindow.print();
                        setTimeout(() => {
                            document.body.removeChild(printFrame);
                        }, 1000);
                    }, 500);
                };
            },
            generateReceiptHTML() {
                let locationName = this.invoice_location_details.name || 
                                  this.invoice_location_details.short_name || 
                                  'FURNITURE MECCA';
                if (this.invoice_location_details.address) {
                    locationName = this.invoice_location_details.address;
                }
                
                const currentDate = new Date().toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                
                const invoiceNumber = this.invoice.invoice_id || 'N/A';
                const salesperson = (this.invoice.username || '').toUpperCase();
                const customerName = this.getCustomerFullName();
                const items = this.invoice.items || [];
                const orderTotal = this.getOrderTotal() || 0;
                const delivery = parseFloat(this.invoice.delivery_charges) || 0;
                const setup = parseFloat(this.invoice.setup_charges) || 0;
                const warranty = parseFloat(this.invoice.warranty_charges) || 0;
                const subtotal = this.getSubTotal ? this.getSubTotal() : 0;
                const salesTax = parseFloat(this.invoice.sales_tax) || 0;
                const grandTotal = subtotal + salesTax;
                const totalPayments = this.get_pms_total ? this.get_pms_total() : 0;
                const balance = grandTotal - totalPayments;
                const predictedTax = this.predictedSalesTax || 0;
                const totalToPay = balance + predictedTax;
                
                let parentPaymentHTML = '';
                if (this.shouldShowParentPayment) {
                    parentPaymentHTML = `
                        <div class="divider"></div>
                        <div style="font-weight: bold; color: #0056b3;">PARENT INVOICE PAYMENT (${this.invoice.parent_invoice_id})</div>
                        ${this.getParentPaymentMethodsForItems.map(pm => `
                            <div class="total-row" style="color: #0056b3;">
                                <span>${(pm.value || '').toUpperCase()} - ${pm.name}:</span>
                                <span>$${pm.display_amount}</span>
                            </div>
                        `).join('')}
                        <div class="total-row" style="font-weight: bold; color: #0056b3; border-top: 1px solid #0056b3;">
                            <span>Total Parent Payment Allocated:</span>
                            <span>$${this.allocatedParentPaymentTotal.toFixed(2)}</span>
                        </div>
                    `;
                }
                
                return `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <title>Receipt - ${invoiceNumber}</title>
                        <style>
                            @page { size: 80mm auto; margin: 0; }
                            body {
                                font-family: 'Courier New', monospace;
                                font-size: 12px;
                                width: 72mm;
                                margin: 0 auto;
                                padding: 2mm;
                                line-height: 1.3;
                            }
                            .header { text-align: center; font-weight: bold; margin-bottom: 3mm; }
                            .store-name { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
                            .store-location { font-size: 12px; margin-top: 1mm; }
                            .divider { border-top: 1px dashed #000; margin: 2mm 0; }
                            .info-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .info-label { font-weight: bold; }
                            .items-header { font-weight: bold; border-bottom: 1px solid #000; margin-top: 3mm; padding-bottom: 1mm; }
                            .item-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .totals { margin-top: 3mm; }
                            .total-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .grand-total { font-weight: bold; font-size: 14px; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 3mm 0; margin: 3mm 0; }
                            .balance { font-weight: bold; font-size: 14px; background: #f0f0f0; padding: 1mm; }
                            .predicted-tax { background: #fff5e6; padding: 1mm; margin: 2mm 0; border-left: 3px solid #ed8936; }
                            .footer { text-align: center; margin-top: 5mm; font-size: 10px; }
                            .thank-you { font-size: 14px; font-weight: bold; margin: 4mm 0; }
                            .cut-line { text-align: center; margin-top: 5mm; font-size: 10px; }
                        </style>
                    </head>
                    <body>
                        <div class="header">
                            <div class="store-name">FURNITURE MECCA</div>
                            <div class="store-location">${locationName}</div>
                            <div class="store-location">${this.invoice_location_details.phone || ''}</div>
                            <div class="store-location">${this.invoice_location_details.email || ''}</div>
                            <div class="store-location">${currentDate}</div>
                        </div>
                        <div class="divider"></div>
                        <div class="info-row"><span class="info-label">INVOICE #:</span><span>${invoiceNumber}</span></div>
                        <div class="info-row"><span class="info-label">SALESPERSON:</span><span>${salesperson}</span></div>
                        <div class="info-row"><span class="info-label">LOCATION:</span><span>${this.packet.locationsdict ? this.packet.locationsdict[this.invoice.location_id] : ''}</span></div>
                        <div class="info-row"><span class="info-label">CUSTOMER:</span><span>${customerName}</span></div>
                        ${this.getCustomerPhone() ? `<div class="info-row"><span class="info-label">PHONE:</span><span>${this.getCustomerPhone()}</span></div>` : ''}
                        ${this.isExchangeInvoice ? `<div class="info-row" style="color: #0056b3; font-weight: bold;"><span>EXCHANGE INVOICE</span><span>Parent: ${this.invoice.parent_invoice_id}</span></div>` : ''}
                        <div class="items-header">ITEM DETAILS</div>
                        ${items.map(item => {
                            const price = parseFloat(item.selling_price) || 0;
                            const qty = (item.quantity - (item.refunded_so_far || 0)) || 1;
                            const discount = parseFloat(item.discount_offered) || 0;
                            const itemTotal = (price * qty) - discount;
                            const desc = item.product_name || item.product_sku || 'Item';
                            return `
                                <div class="item-row"><span>${desc.substring(0, 30)}${desc.length > 30 ? '...' : ''}</span><span>$${itemTotal.toFixed(2)}</span></div>
                                <div style="font-size: 10px; margin-left: 2mm; margin-bottom: 1mm;">${item.product_sku} | $${price.toFixed(2)} x ${qty}${discount > 0 ? ` (-$${discount.toFixed(2)})` : ''}</div>
                            `;
                        }).join('')}
                        <div class="divider"></div>
                        <div class="totals">
                            <div class="total-row"><span>ORDER TOTAL:</span><span>$${orderTotal.toFixed(2)}</span></div>
                            <div class="total-row"><span>DELIVERY:</span><span>$${delivery.toFixed(2)}</span></div>
                            <div class="total-row"><span>SETUP:</span><span>$${setup.toFixed(2)}</span></div>
                            <div class="total-row"><span>WARRANTY:</span><span>$${warranty.toFixed(2)}</span></div>
                            <div class="total-row"><span>SUBTOTAL:</span><span>$${subtotal.toFixed(2)}</span></div>
                            <div class="total-row"><span>SALES TAX:</span><span>$${salesTax.toFixed(2)}</span></div>
                            <div class="grand-total total-row"><span>GRAND TOTAL:</span><span>$${grandTotal.toFixed(2)}</span></div>
                        </div>
                        ${this.invoice.payment_methods && this.invoice.payment_methods.length > 0 ? `
                            <div class="divider"></div>
                            <div style="font-weight: bold; margin-bottom: 1mm;">PAYMENTS</div>
                            ${this.invoice.payment_methods.map(pm => `
                                <div class="total-row"><span>${(pm.value || '').toUpperCase()}:</span><span>$${(parseFloat(pm.amount) || 0).toFixed(2)}</span></div>
                            `).join('')}
                            <div class="total-row" style="font-weight: bold;"><span>TOTAL PAID:</span><span>$${totalPayments.toFixed(2)}</span></div>
                        ` : ''}
                        ${parentPaymentHTML}
                        <div class="divider"></div>
                        <div class="balance total-row"><span>CURRENT BALANCE:</span><span>$${balance.toFixed(2)}</span></div>
                        ${predictedTax > 0 ? `
                            <div class="predicted-tax total-row">
                                <span>PREDICTED SALES TAX (${(parseFloat(this.invoice.tax_perc) * 100).toFixed(2)}%):</span>
                                <span>$${predictedTax.toFixed(2)}</span>
                            </div>
                            <div class="total-row" style="font-weight: bold; font-size: 14px; margin-top: 2mm;">
                                <span>TOTAL TO PAY:</span>
                                <span>$${totalToPay.toFixed(2)}</span>
                            </div>
                        ` : ''}
                        ${this.invoice.is_layaway ? `<div style="text-align: center; font-weight: bold; color: red; margin: 3mm 0;">LAYAWAY SALE</div>` : ''}
                        <div class="footer"><div class="thank-you">THANK YOU!</div><div>${this.invoice_location_details.website || 'www.myfurnituremecca.com'}</div></div>
                        <div class="cut-line">- - - - - - - - - - - - - - - - - - - -</div>
                    </body>
                    </html>
                `;
            },
            showParentFullImage(parentItem) {
                if (!parentItem) return;
                const imageUrl = this.parentImageForGroup(parentItem.parent_product_sku);
                if (imageUrl) {
                    this.$bvModal.show('modal-image-viewer');
                    this.$nextTick(() => {
                        this.currentFullSizeImage = imageUrl;
                        this.currentProductName = parentItem.parent_product_name || parentItem.parent_product_sku || 'Parent Product Image';
                    });
                } else {
                    this.$bvToast.toast("No parent product image available", {title: "Info", autoHideDelay: 2000, variant:"info"});
                }
            },
            getPaymentByMethod(methodValue) {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return '0.00';
                }
                const total = this.invoice.payment_methods
                    .filter(pm => pm.value === methodValue)
                    .reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
                return total.toFixed(2);
            },
            getLayawayPayments() {
                if (!this.invoice.payment_methods || this.invoice.payment_methods.length === 0) {
                    return [];
                }
                if (this.invoice.is_layaway) {
                    return this.invoice.payment_methods.map(pm => ({
                        method: pm.value,
                        amount: pm.amount,
                        ts: pm.ts
                    }));
                }
                return [];
            },
            showDamageFullImage(item) {
                if (!item.is_damaged) return;
                const imageUrl = this.getDamageHistoryImage(item);
                if (imageUrl) {
                    this.$bvModal.show('modal-image-viewer');
                    this.$nextTick(() => {
                        this.currentFullSizeImage = imageUrl;
                        this.currentProductName = `${item.product_name || item.product_sku} - Damage Condition`;
                    });
                } else {
                    this.showFullImage(item);
                }
            },
            getDamageHistoryImage(item) {
                if (!item.is_damaged || !item.damage_history || item.damage_history.length === 0) {
                    return null;
                }
                const damageRecord = item.damage_history.find(record => record.images && record.images.length > 0);
                if (!damageRecord || !damageRecord.images || damageRecord.images.length === 0) {
                    return null;
                }
                const damageImage = damageRecord.images[0];
                if (!damageImage || !damageImage.image_url) {
                    return null;
                }
                const imageUrl = damageImage.image_url.trim();
                if (imageUrl && imageUrl.includes('blob.core.windows.net')) {
                    const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(imageUrl)}`;
                    return proxyUrl;
                }
                return imageUrl;
            },
            handleDamageImageError(event, item) {
                console.warn(`Damage image failed to load for ${item.product_sku}:`, event.target.src);
                const normalImage = this.getProductImage(item);
                if (normalImage) {
                    event.target.src = normalImage;
                    event.target.alt = item.product_name || item.product_sku;
                } else {
                    const parent = event.target.parentElement;
                    if (parent) {
                        const existingFallback = parent.querySelector('.no-image-placeholder');
                        if (!existingFallback) {
                            parent.innerHTML = `
                                <div class="no-image-placeholder" style="width: 60px; height: 50px; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 1px dashed #ddd; border-radius: 4px; background: #f9f9f9;">
                                    <div style="font-size: 20px; margin-bottom: 5px;">📷</div>
                                    <div style="color: #999; font-size: 10px;">No Damage Image</div>
                                </div>
                            `;
                        }
                    }
                    event.target.style.display = 'none';
                }
            },
            handleDamageImageLoad(event, item) {
                console.log(`Damage image loaded successfully for ${item.product_sku}`);
            },
            showAsIsImage(item) {
                if (!item || !item.as_is_image_url) {
                    this.$bvToast.toast("No As-Is image available for this product", {title: "Info", autoHideDelay: 2000, variant:"info"});
                    return;
                }
                const imageUrl = item.as_is_image_url.trim();
                let finalImageUrl = imageUrl;
                if (imageUrl && imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    finalImageUrl = `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                }
                this.$bvModal.show('modal-image-viewer');
                this.$nextTick(() => {
                    this.currentFullSizeImage = finalImageUrl;
                    this.currentProductName = `${item.product_name || item.product_sku} - As-Is Condition`;
                });
            },
            getNormalProductImage(item) {
                if (!item || !item.image_url) {
                    return null;
                }
                const imageUrl = item.image_url.trim();
                if (imageUrl && imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    const proxyUrl = `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                    return proxyUrl;
                }
                return imageUrl;
            },
            getProductImage(item) {
                if (item.is_damaged) {
                    const damageImage = this.getDamageHistoryImage(item);
                    if (damageImage) {
                        return damageImage;
                    }
                }
                return this.getNormalProductImage(item);
            },
            handleImageError(event, item) {
                console.warn(`Image failed to load for ${item.product_sku}:`, event.target.src);
                const parent = event.target.parentElement;
                if (parent) {
                    const existingFallback = parent.querySelector('.no-image-placeholder');
                    if (!existingFallback) {
                        parent.innerHTML = `
                            <div class="no-image-placeholder" style="width: 60px; height: 50px; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 1px dashed #ddd; border-radius: 4px; background: #f9f9f9;">
                                <div style="font-size: 20px; margin-bottom: 5px;">📷</div>
                                <div style="color: #999; font-size: 10px;">No Image</div>
                                <div style="color: #ccc; font-size: 8px; margin-top: 2px;">${item.product_sku}</div>
                            </div>
                        `;
                    }
                }
                event.target.style.display = 'none';
            },
            handleImageLoad(event, item) {
                const sku = (item && item.product_sku) ? item.product_sku : 'unknown';
                console.log(`Image loaded successfully for ${sku}`);
            },
            showFullImage(item) {
                if (!item) return;
                const imageUrl = this.getProductImage(item);
                if (imageUrl) {
                    this.$bvModal.show('modal-image-viewer');
                    this.$nextTick(() => {
                        this.currentFullSizeImage = imageUrl;
                        this.currentProductName = item.product_name || item.product_sku || 'Product Image';
                    });
                } else {
                    this.$bvToast.toast("No image available for this product", {title: "Info", autoHideDelay: 2000, variant:"info"});
                }
            },
            hasBackorderLocsub(item) {
                if (!item || item.locsub === undefined || item.locsub === null) {
                    return false;
                }
                const itemLocsub = String(item.locsub).trim().replace(/\s+/g, '');
                return itemLocsub === '46|122';
            },
            isParentReturned(parentGroup) {
                if (!parentGroup || parentGroup.length === 0) return false;
                return parentGroup.every(item => this.is_returned(item));
            },
            is_returned(item) {
                return ((item.quantity - item.refunded_so_far) == 0);
            },
            check_if_edit_allowed() {
                if (!this.packet.userDetails) return false;
                let current_username = this.packet.userDetails.username;
                let current_userrole = this.packet.userDetails.userrole;
                let current_location_id = parseInt((this.packet.userDetails.properties || {}).location_id);
                let invoice_location_id = parseInt(this.invoice.location_id);
                let status = false;
                if (current_userrole === 'admin') {
                    status = true;
                } else {
                    if (current_userrole === 'locationadmin' && current_location_id === invoice_location_id) {
                        status = true;
                    }
                }
                return status;
            },
            close_layaway() {
                this.$bvModal.hide("modal-layaway-payments");
                this.reload_invoice();
            },
            open_layaway_payment_modal() {
                this.$bvModal.show("modal-layaway-payments");
            },
            payment_ts_format(ts) {
                let p = ts.split("T");
                let p1 = p[0];
                let p2 = p[1].split(".")[0];
                return p1 + " " + p2;
            },
            remove_refund_item(rtitdx) {
                this.refunds.items_to_be_refunded.splice(rtitdx, 1);
            },
            remove_rpm(rpidx) {
                this.refunds.refund_methods.splice(rpidx, 1);
            },
            get_time() {
                return new moment().format("MM-DD-YYYY (HH:MM:SS)");
            },
            get_payment_methods_sum() {
                let tot = 0.0;
                for (var i = 0; i < this.refunds.refund_methods.length; i++) {
                    tot += (parseFloat(this.refunds.refund_methods[i].amount) || 0.0);
                }
                return tot;
            },
            get_refunds_packet() {
                return {
                    total_delivery_refund: 0.0,
                    total_setup_refund: 0.0,
                    total_warranty_refund: 0.0,
                    total_sales_tax_refund: 0.0,
                    refund_method_options: [],
                    refund_methods: [],
                    items_to_be_refunded: [],
                };
            },
            submit_refund() {
                let that = this;
                let total_being_refunded = this.get_total_to_be_refunded();
                let total_paid_on_taxable_rpms = this.sum(this.refunds.refund_methods.filter(function(rpm) {
                    return that.check_rpm_charges_tax(rpm) == false;
                }).map(function(rpm) {
                    return (parseFloat(rpm.amount) || 0.00);
                }));
                let total_paid_on_non_taxable_rpms = this.sum(this.refunds.refund_methods.filter(function(rpm) {
                    return that.check_rpm_charges_tax(rpm) == true;
                }).map(function(rpm) {
                    return (parseFloat(rpm.amount) || 0.00);
                }));
                let total_sales_tax_being_refunded = total_paid_on_taxable_rpms * parseFloat(this.invoice.tax_perc);
                this.distribute_sales_tax(total_sales_tax_being_refunded);
                
                let data = {
                    invoice_id: this.invoice.invoice_id,
                    created_by: this.packet.userDetails ? this.packet.userDetails.username : '',
                    approved: true,
                    reviewed_by: this.packet.userDetails ? this.packet.userDetails.username : '',
                    total_refunded_item_amount: total_being_refunded,
                    total_refunded_sales_tax: total_sales_tax_being_refunded,
                    total_refunded_delivery: (parseFloat(this.refunds.total_delivery_refund) || 0.00),
                    total_refunded_setup: (parseFloat(this.refunds.total_setup_refund) || 0.00),
                    total_refunded_warranty: (parseFloat(this.refunds.total_warranty_refund) || 0.00),
                    refund_methods: this.refunds.refund_methods,
                    refund_items: this.refunds.items_to_be_refunded.map(function(d) {
                        return {
                            refund_id: null,
                            invoice_id: null,
                            product_id: d.product_id,
                            is_special: d.is_special,
                            product_sku: d.product_sku,
                            itemid: d.itemid,
                            return_quantity: parseInt(d.return_quantity),
                            delivered: d.delivered,
                            return_reason: d.return_reason,
                            return_locsub: d.return_locsub,
                            selling_price: d.selling_price,
                            refunded_at_state: d.state,
                        }
                    })
                };
                let path = "/api/sales/record_refund/";
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("Recorded refund.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                        that.reload_invoice();
                    }
                    that.items_being_loaded = false;
                };
                let f = function(error) {
                    that.$bvToast.toast("Error registering refund. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.items_being_loaded = false;
                };
                this.items_being_loaded = true;
                Requestor.postAsyncP(path, data, this, s, f);
            },
            check_rpm_charges_tax(rpmethod) {
                let status = false;
                if (!rpmethod.value) {
                    return status;
                }
                let value = rpmethod.value.split("|")[0];
                let name = rpmethod.value.split("|")[1];
                if (value == "store_credit") {
                    status = true;
                } else {
                    let pmet = this.invoice.payment_methods.filter(function(d) {
                        return d.value == value && d.name == name;
                    });
                    if (pmet.length > 0) {
                        pmet = pmet[0];
                        if (pmet.charged_tax_itself == true) {
                            status = true;
                        }
                    }
                }
                return status;
            },
            get_max_allowed_on_refund_payment_method(rpmethod) {
                let v = 0.0;
                let b = 0.0;
                if ((rpmethod.value || "").split("|").length == 2) {
                    let pm = rpmethod.value.split("|")[0];
                    let mn = rpmethod.value.split("|")[1];
                    let ipm = this.invoice.payment_methods.filter(function(d) {
                        return d.value == pm && d.name == mn;
                    });
                    if (ipm.length > 0) {
                        let sipm = ipm[0];
                        if (sipm) {
                            v = sipm.amount;
                        }
                    }
                    if (this.invoice.refund_methods && this.invoice.refund_methods.length > 0) {
                        let refund_methods = this.invoice.refund_methods;
                        for (var i = 0; i < refund_methods.length; i++) {
                            var ri = refund_methods[i].filter(function(d) {
                                return d.value.split("|")[0] == pm && d.value.split("|")[1] == mn
                            });
                            if (ri.length > 0) {
                                for (var j = 0; j < ri.length; j++) {
                                    var jri = ri[j];
                                    let amount = (parseFloat(jri.amount) || 0.0);
                                    b += amount;
                                }
                            }
                        }
                    }
                    return (v - b) * 1.0;
                }
            },
            add_refund_payment_method() {
                if (this.refunds.refund_method_options.length == 0) {
                    let pms = this.invoice.payment_methods || [];
                    for (var i = 0; i < pms.length; i++) {
                        let pm = pms[i];
                        this.refunds.refund_method_options.push({
                            value: pm.value + "|" + pm.name,
                            text: pm.value + " - " + pm.name
                        });
                    }
                    this.refunds.refund_method_options.push({
                        value: "store_credit" + "|" + this.invoice.invoice_id,
                        text: "store_credit" + " - " + this.invoice.invoice_id + " (CURRENT INVOICE)"
                    });
                }
                this.refunds.refund_methods.push({
                    value: null,
                    amount: 0.0,
                    sales_tax: 0.0,
                });
            },
            get_total_refund_dollar_value() {
                let x = (this.get_total_to_be_refunded() || 0.0) + parseFloat(this.refunds.total_delivery_refund || 0.0) + parseFloat(this.refunds.total_setup_refund || 0.0) + parseFloat(this.refunds.total_warranty_refund || 0.0);
                return x;
            },
            get_total_to_be_refunded() {
                let k = 0.0;
                if (this.refunds.items_to_be_refunded.length > 0) {
                    k += this.sum(this.refunds.items_to_be_refunded.map(function(d) {
                        return ((d.selling_price * d.return_quantity) - (d.unit_discount_value * d.return_quantity));
                    }));
                }
                return k;
            },
            get_total_delivery_refund_remaining() {
                let original_delivery_charges = (this.invoice.delivery_charges || 0.0);
                let so_far_refunded = (this.invoice.total_refunded_delivery || 0.0);
                return (original_delivery_charges - so_far_refunded);
            },
            get_total_setup_refund_remaining() {
                let original_setup_charges = (this.invoice.setup_charges || 0.0);
                let so_far_refunded = (this.invoice.total_refunded_setup || 0.0);
                return (original_setup_charges - so_far_refunded);
            },
            load_cs_issues(item) {
                let id = item.cs_ticket_id;
                this.cs_item_data = { 
                    id: id, 
                    invoice_id: item.invoice_id, 
                    product_sku: item.product_sku, 
                    itemid: item.itemid 
                };
                this.$bvModal.show("modal-cs-ticket");
            },
            complete_refund(invoice_id) {
                let data = { invoice_id: invoice_id };
                let path = "/api/sales/complete_refund/";
                let that = this;
                let s = function(response) {
                    if (response.data.errror) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.reload_invoice();
                    }
                    that.invoice_being_loaded = false;
                };
                let f = function(error) {
                    that.$bvToast.toast("Error completing refund. Please try later.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.invoice_being_loaded = false;
                };
                this.invoice_being_loaded = true;
                Requestor.postSyncP(path, data, this, s, f);
            },
            update_invoice_customer(e) {
                if (e) {
                    e.preventDefault();
                }
                let path = "/api/sales/update_invoice_customer/";
                let data = { invoice_id: this.invoice.invoice_id, customer: this.invoice.customer };
                let that = this;
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("Updated.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                    }
                };
                let f = function(error) {
                    that.$bvToast.toast("Error updating customer info. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },
            update_item_level(item) {
                let that = this;
                if (item.order_type === 'delivery_from_store') {
                    this.handleDeliveryFromStore(item);
                }
                let data = {
                    invoice_id: item.invoice_id,
                    itemid: item.itemid,
                    product_sku: item.product_sku,
                    delivery_date: item.delivery_date,
                    order_type: item.order_type,
                    pull_date: moment(item.delivery_date).add(-1, 'day').format("MM-DD-YYYY"),
                    username: this.packet.userDetails ? this.packet.userDetails.username : ''
                };
                let path = "/api/sales/update_item_level/";
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.$bvToast.toast("UPDATED ITEM DATA.", {title: "Success", autoHideDelay: 1500, variant: "success"});
                    }
                };
                let f = function(error) {
                    that.$bvToast.toast("Error updating delivery date. Please try again.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                };
                Requestor.postAsyncP(path, data, this, s, f);
            },
            get_invoice_location_address() {
                let location_id = this.invoice.location_id;
                let location = this.packet.locations ? this.packet.locations.filter(function(d) { return d.value == location_id }) : [];
                if (location.length > 0) {
                    location = location[0];
                    this.invoice_location_details.address = location.address;
                    this.invoice_location_details.name = location.name;
                    this.invoice_location_details.email = location.email;
                    this.invoice_location_details.phone = location.phone;
                    this.invoice_location_details.location_address = location.location_address;
                    this.invoice_location_details.short_name = location.short_name;
                    this.invoice_location_details.website = "www.myfurnituremecca.com";
                    this.invoice_location_details.city = location.city || '';
                    this.invoice_location_details.state = location.state || '';
                    this.invoice_location_details.zipcode = location.zipcode || '';
                }
            },
            fetch_prereqs() {
                let that = this;
                let prereqs = [
                    {
                        path: "/api/inventory/get_all_loc_inventory_dict/",
                        data: {},
                        type: "",
                        executable: Requestor.getAsync,
                        s: function(response) {
                            if (response.data.result) {
                                that.all_loc_inventory_dict = response.data.result;
                            } else {
                                that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                            }
                        },
                        f: function(error) {
                            that.$bvToast.toast("Error loading inventory.", {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        }
                    },
                    {
                        path: "/api/policy/get_policy/",
                        data: {},
                        type: "secure",
                        executable: Requestor.getAsyncP,
                        s: function(response) {
                            if (response.data.result) {
                                that.policy_text = response.data.result[0].text;
                            } else {
                                that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                            }
                        },
                        f: function(error) {
                            that.$bvToast.toast("Error loading terms and conditions.", {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        }
                    },
                    {
                        path: "/api/finance_company/get_all_finance_companies/",
                        data: {},
                        type: "secure",
                        executable: Requestor.getAsyncP,
                        s: function(response) {
                            if (response.data.result) {
                                that.finance_companies = response.data.result.map(function(d) {
                                    return { "value": d.finance_company_name, "text": d.finance_company_name, "phone_number": d.finance_company_phone_number, "company_charges_tax_in_states": d.finance_company_charges_tax_in_states }
                                });
                            } else {
                                that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                            }
                        },
                        f: function(error) {
                            console.log(error, 'C');
                            that.$bvToast.toast("Error loading finance companies.", {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        }
                    },
                ];
                for (var i = 0; i < prereqs.length; i++) {
                    let path = prereqs[i]["path"];
                    let data = prereqs[i]["data"];
                    let s = prereqs[i]["s"];
                    let f = prereqs[i]["f"];
                    let type = prereqs[i]["type"];
                    if (type == "secure") {
                        prereqs[i]["executable"](path, data, this, s, f);
                    } else {
                        prereqs[i]["executable"](path, data, s, f);
                    }
                }
            },
            print_it() {
                window.print();
            },
            // MODIFIED: reload_invoice to handle parent invoice data and exchange invoices
            reload_invoice() {
                let path = "/api/sales/get_invoice/";
                let data = { invoice_id: this.invoice.invoice_id };
                let that = this;
                
                this.startLoadingAnimation();
                
                let s = function(response) {
                    if (response.data.result) {
                        that.invoice = response.data.result;
                        
                        // Ensure customer object exists
                        if (!that.invoice.customer) {
                            that.invoice.customer = {
                                fullname: '',
                                address_line_1: '',
                                address_line_2: '',
                                phone: '',
                                phone_2: '',
                                email: '',
                                city: '',
                                state: '',
                                zipcode: '',
                                floor: ''
                            };
                        }
                        
                        that.lease_id = that.invoice.lease_id || null;
                        that.misc_info = that.invoice.misc_info || null;
                        
                        that.termsAgreed = that.invoice.terms_agreed === true || that.invoice.terms_agreed === 'true' || that.invoice.terms_agreed === 1 || that.invoice.terms_agreed === '1';
                        that.warrantyRefused = that.invoice.warranty_refused === true || that.invoice.warranty_refused === 'true' || that.invoice.warranty_refused === 1 || that.invoice.warranty_refused === '1';
                        that.deliveryWaiverAgreed = that.invoice.delivery_waiver_agreed === true || that.invoice.delivery_waiver_agreed === 'true' || that.invoice.delivery_waiver_agreed === 1 || that.invoice.delivery_waiver_agreed === '1';
                        that.spacePreparationAgreed = that.invoice.space_preparation_agreed === true || that.invoice.space_preparation_agreed === 'true' || that.invoice.space_preparation_agreed === 1 || that.invoice.space_preparation_agreed === '1';
                        that.addressConfirmationAgreed = that.invoice.address_confirmation_agreed === true || that.invoice.address_confirmation_agreed === 'true' || that.invoice.address_confirmation_agreed === 1 || that.invoice.address_confirmation_agreed === '1';
                        that.deliveryDamageWaiverAgreed = that.invoice.delivery_damage_waiver_agreed === true || that.invoice.delivery_damage_waiver_agreed === 'true' || that.invoice.delivery_damage_waiver_agreed === 1 || that.invoice.delivery_damage_waiver_agreed === '1';
                        
                        that.fetchTicketsForInvoice();
                        
                        // Parse parent_invoice_data if it's a string
                        let parentData = that.invoice.parent_invoice_data;
                        
                        if (typeof parentData === 'string') {
                            try {
                                parentData = JSON.parse(parentData);
                                that.invoice.parent_invoice_data = parentData;
                            } catch(e) {
                                console.error('Failed to parse parent_invoice_data:', e);
                                parentData = null;
                            }
                        }
                        
                        // Use parent_invoice_data from backend
                        if (parentData && parentData !== 'null' && parentData !== null) {
                            that.parentInvoiceData = parentData;
                            that.parentInvoicePaymentMethods = parentData.payment_methods || [];
                            that.showParentPaymentInfo = that.parentInvoicePaymentMethods.length > 0;
                        } else {
                            // Fallback: try to fetch parent invoice if not provided by backend
                            if (that.invoice.invoice_type === 'EX' && that.invoice.parent_invoice_id) {
                                that.fetchParentInvoice();
                            } else {
                                that.parentInvoiceData = null;
                                that.parentInvoicePaymentMethods = [];
                                that.showParentPaymentInfo = false;
                            }
                        }
                        
                        if (!that.invoice.invoice_id) {
                            that.message = "No invoice found.";
                        }
                        
                        that.stopLoadingAnimation();
                    } else {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                        that.stopLoadingAnimation();
                    }
                    that.invoice_being_loaded = false;
                };
                
                let f = function(error) {
                    console.log(error);
                    that.$bvToast.toast("Error loading invoice. Please try again later.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.invoice_being_loaded = false;
                    that.stopLoadingAnimation();
                };
                
                this.refunds = this.get_refunds_packet();
                this.invoice_being_loaded = true;
                Requestor.postAsyncP(path, data, this, s, f);
            },
            // Fallback method to fetch parent invoice if backend doesn't provide it
            fetchParentInvoice() {
                if (!this.isExchangeInvoice) return;
                
                const parentInvoiceId = this.invoice.parent_invoice_id;
                if (!parentInvoiceId) return;
                
                const that = this;
                const path = "/api/sales/get_invoice/";
                const data = { invoice_id: parentInvoiceId };
                
                const s = function(response) {
                    if (response.data.result) {
                        that.parentInvoiceData = response.data.result;
                        that.parentInvoicePaymentMethods = response.data.result.payment_methods || [];
                        that.showParentPaymentInfo = that.parentInvoicePaymentMethods.length > 0;
                        that.$forceUpdate();
                    }
                };
                
                const f = function(error) {
                    console.error("Error fetching parent invoice:", error);
                };
                
                Requestor.postAsyncP(path, data, this, s, f);
            },
            request_refund(invoice_id) {
                let that = this;
                let data = { invoice_id: invoice_id, requesting_username: this.packet.userDetails ? this.packet.userDetails.username : '' };
                let path = "/api/sales/request_refund/";
                let s = function(response) {
                    if (response.data.errror) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant: "warning"});
                    } else {
                        that.invoice.refund_requested = true;
                        that.invoice.refund_request_approved = response.data.result[0].approved;
                    }
                    that.invoice_being_loaded = false;
                };
                let f = function(error) {
                    console.log(error);
                    that.$bvToast.toast("Error requesting a refund. Please try later.", {title: "Error", autoHideDelay: 1500, variant: "danger"});
                    that.invoice_being_loaded = false;
                };
                this.invoice_being_loaded = true;
                Requestor.getAsyncP(path, data, this, s, f);
            },
            get_quantity_dd(quantity) {
                return [...Array(quantity).keys()].map(function(d) { return { value: d + 1, text: d + 1 } });
            },
            sum(items) {
                return items.reduce((pv, cv) => parseFloat(pv) + parseFloat(cv), 0.0);
            },
            edit_invoice() {
                this.edit_invoice_action = !this.edit_invoice_action;
            },
            summarize_refunds_history(refunds) {
                let total_refunded_value = 0.0;
                for (var i = 0; i < refunds.length; i++) {
                    let refund = refunds[i];
                    if (refund.detail.type == 'return') {
                        let value = refund.detail.total_refunded_item_amount + refund.detail.total_refunded_sales_tax + refund.detail.total_refunded_delivery + refund.detail.total_refunded_setup + refund.detail.total_refunded_warranty;
                        total_refunded_value += (value || 0.0);
                    }
                }
                this.total_refunded_value = (total_refunded_value || 0.0);
            },
            distribute_sales_tax(total_sales_tax_being_refunded) {
                // This method can be implemented as needed
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.packet || '{}');
            this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
            this.edit_invoice_action = false;
            
            this.startLoadingAnimation();
            
            if (this.viewdata) {
                this.invoice = this.viewdata;
                
                // Ensure customer object exists
                if (!this.invoice.customer) {
                    this.invoice.customer = {
                        fullname: '',
                        address_line_1: '',
                        address_line_2: '',
                        phone: '',
                        phone_2: '',
                        email: '',
                        city: '',
                        state: '',
                        zipcode: '',
                        floor: ''
                    };
                }
                
                this.termsAgreed = this.invoice.terms_agreed === true || this.invoice.terms_agreed === 'true' || this.invoice.terms_agreed === 1 || this.invoice.terms_agreed === '1';
                this.warrantyRefused = this.invoice.warranty_refused === true || this.invoice.warranty_refused === 'true' || this.invoice.warranty_refused === 1 || this.invoice.warranty_refused === '1';
                this.deliveryWaiverAgreed = this.invoice.delivery_waiver_agreed === true || this.invoice.delivery_waiver_agreed === 'true' || this.invoice.delivery_waiver_agreed === 1 || this.invoice.delivery_waiver_agreed === '1';
                this.spacePreparationAgreed = this.invoice.space_preparation_agreed === true || this.invoice.space_preparation_agreed === 'true' || this.invoice.space_preparation_agreed === 1 || this.invoice.space_preparation_agreed === '1';
                this.addressConfirmationAgreed = this.invoice.address_confirmation_agreed === true || this.invoice.address_confirmation_agreed === 'true' || this.invoice.address_confirmation_agreed === 1 || this.invoice.address_confirmation_agreed === '1';
                this.deliveryDamageWaiverAgreed = this.invoice.delivery_damage_waiver_agreed === true || this.invoice.delivery_damage_waiver_agreed === 'true' || this.invoice.delivery_damage_waiver_agreed === 1 || this.invoice.delivery_damage_waiver_agreed === '1';
                this.get_invoice_location_address();
                this.fetchTicketsForInvoice();
                
                let parentData = this.invoice.parent_invoice_data;
                
                if (typeof parentData === 'string') {
                    try {
                        parentData = JSON.parse(parentData);
                        this.invoice.parent_invoice_data = parentData;
                    } catch(e) {
                        console.error('Failed to parse parent_invoice_data in created:', e);
                        parentData = null;
                    }
                }
                
                if (parentData && parentData !== 'null' && parentData !== null) {
                    this.parentInvoiceData = parentData;
                    this.parentInvoicePaymentMethods = parentData.payment_methods || [];
                    this.showParentPaymentInfo = this.parentInvoicePaymentMethods.length > 0;
                }
                
                setTimeout(() => {
                    this.stopLoadingAnimation();
                }, 300);
            } else {
                this.reload_invoice();
            }
            this.fetch_prereqs();
        }
    }
</script>

<style scoped>
    .product-thumbnail {
        transition: transform 0.2s ease-in-out;
        border: 2px solid #dee2e6;
    }
    .product-thumbnail:hover {
        transform: scale(1.05);
        border-color: #007bff;
    }
    .product-image-container {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 60px;
    }
    .ipt_tdr {
        vertical-align: middle;
    }
    td {
        font-size: 13px;
        background: inherit;
    }
    .default-input {
        padding: 0px !important;
    }
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    input[type=number] {
        -moz-appearance: textfield;
    }
    .image-container {
        position: relative;
        display: inline-block;
    }
    .no-image-placeholder {
        width: 60px;
        height: 50px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border: 1px dashed #ddd;
        border-radius: 4px;
        background: #f9f9f9;
    }
    .product-image {
        transition: transform 0.2s ease;
    }
    .product-image:hover {
        transform: scale(1.05);
    }
    .separate3 h5 {
        color: #2c3e50 !important;
        font-size: 1.2rem;
        font-weight: bold;
        margin-bottom: 15px;
        padding: 10px 15px;
        background: #f8f9fa;
        border-left: 0.5px solid lightgray;
        border-radius: 4px;
    }
    .separate3 > div {
        border: 2px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
        background-color: #ffffff;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
    /* OUTLINED SPIFF Badge Styles */
    .spiff-badge-outline {
        background: transparent !important;
        color: #28a745 !important;
        padding: 2px 8px !important;
        border-radius: 12px !important;
        font-size: 10px !important;
        font-weight: bold !important;
        display: inline-block !important;
        border: 2px solid #28a745 !important;
        cursor: default !important;
        transition: all 0.3s ease;
    }
    
    .spiff-badge-outline:hover {
        background: #28a745 !important;
        color: white !important;
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
    }
    
    /* Product row conditional outline styling */
    .product-row {
        transition: all 0.3s ease;
    }
    
    .product-row:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
    
    /* Product row with spiff hover effect */
    .product-row[style*="border: 2px solid #28a745"]:hover {
        box-shadow: 0 2px 12px rgba(40, 167, 69, 0.25) !important;
    }
    
    @media print {
        .spiff-badge-outline {
            display: none !important;
        }
    }
    
    @media screen {
        .print-header-container {
            display: block;
        }
        .print-header {
            display: none;
        }
        .screen-only {
            display: block;
        }
        .print-only {
            display: none !important;
        }
        .print-only-policy {
            display: none;
        }
    }
    @media print {
        .screen-only,
        .hidden-print,
        .btn,
        .b-button,
        button,
        .b-modal,
        .b-alert:not(.print-keep),
        [class*="modal"],
        .bv-example-row,
        .b-card-text .btn,
        .b-card-text button {
            display: none !important;
        }
        .print-only {
            display: inline-block !important;
        }
        .print-header-container {
            display: table !important;
            width: 100%;
        }
        .print-header-container thead {
            display: table-header-group !important;
        }
        .print-header-container tbody {
            display: table-row-group !important;
        }
        .print-header {
            display: block !important;
            background: white;
            padding: 0;
            margin: 0;
            margin-left: 5%;
            width: 90%;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
        }
        th:nth-child(5),
        th:nth-child(12),
        th:nth-child(13),
        td:nth-child(5),
        td:nth-child(12),
        td:nth-child(13) {
            display: none !important;
        }
        .lease-info-print {
            display: block !important;
            margin-top: 10px;
            padding: 5px;
            border: 1px solid #ccc;
            font-size: 10px;
        }
        table {
            width: 100% !important;
            table-layout: fixed !important;
            font-size: 10px !important;
        }
        td, th {
            font-size: 13px !important;
            padding: 3px 2px !important;
            white-space: normal !important;
            word-wrap: break-word !important;
        }
        .product-image, .image-container img {
            max-width: 40px !important;
            max-height: 40px !important;
        }
        .mx-datepicker {
            display: none !important;
        }
        td[data-date-value]::before {
            content: attr(data-date-value);
            display: inline-block;
            font-size: 13px;
        }
        .print-only-policy {
            display: block !important;
            page-break-before: always !important;
        }
        .separate3:has(h5) {
            display: none !important;
        }
        table {
            page-break-inside: avoid;
        }
        tr, td, th {
            page-break-inside: avoid;
        }
        body {
            margin: 0.25in;
            padding: 0 !important;
        }
        .print-content {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
        }
        .separate2 {
            width: 100% !important;
            max-width: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
        }
        .separate2 table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            border-spacing: 20px 0px !important;
        }
        .separate2 table th,
        .separate2 table td {
            width: 33.33% !important;
            max-width: 33.33% !important;
            overflow: hidden !important;
            word-wrap: break-word !important;
            font-size: 13px !important;
        }
        .separate2 table table {
            width: 100% !important;
            table-layout: fixed !important;
            padding: 5px !important;
        }
        .separate2 table table td {
            width: 100% !important;
            max-width: 100% !important;
            padding: 3px;
            font-size: 13px !important;
        }
        .separate2 .btn,
        .separate2 button {
            display: none !important;
        }
        .child-product-row .image-container {
            transform: scale(0.75);
            transform-origin: center;
        }
        .group-product-header td {
            font-size: 15px !important;
            padding: 3px !important;
        }
    }
    h3 {
        margin: 0px;
    }
    .input-txt {
        border: 0px;
        font-weight: normal;
        color: #2c3e50;
        display: inline;
        padding: 0px;
        margin: 0px;
    }
    .ipt_tdhr {
        border: 0.5px solid lightgray;
    }
    table.report-container {
        page-break-after: always;
    }
    thead.report-header {
        display: table-header-group;
    }
    tfoot.report-footer {
        display: table-footer-group;
    }
    .image-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }
    .image-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
        cursor: pointer;
        font-size: 16px;
    }
    .image-container:hover .image-overlay {
        opacity: 1;
    }
    .ipt_tdr {
        vertical-align: middle;
        overflow: hidden;
    }
    .parent-product-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 10px;
    }
    .print-header-container {
        width: 100%;
    }
    .print-header-container thead {
        display: table-header-group;
    }
    .print-header-container tbody {
        display: table-row-group;
    }
    .print-header-container td {
        padding: 0;
    }
    .child-product-row .image-container {
        transform: scale(0.85) translateX(-10%);
        transform-origin: right center;
    }
    .child-product-row .no-image-placeholder {
        transform: scale(0.85) translateX(-10%);
        transform-origin: right center;
    }
    .child-product-row td:first-child {
        overflow: visible;
    }
    .child-product-row td {
        border-left: 0.5px solid lightgray !important;
    }
    @media print {
        .child-product-row .image-container {
            transform: scale(0.85);
            transform-origin: right center;
        }
        .child-product-row .no-image-placeholder {
            transform: scale(0.85);
            transform-origin: right center;
        }
    }
    tr.group-product-header td {
        border-top: 2px solid #a1a1a1 !important;
    }
    tr.group-product-header + tr.child-product-row:last-of-type td {
        border-bottom: 2px solid #a1a1a1 !important;
    }
    tr.group-product-header {
        border-top: 2px solid #a1a1a1 !important;
        border-left: 2px solid #a1a1a1 !important;
        border-right: 2px solid #a1a1a1 !important;
    }
    tr.group-product-header ~ tr.child-product-row:not(:last-of-type) {
        border-left: 2px solid #a1a1a1 !important;
        border-right: 2px solid #a1a1a1 !important;
    }
    tr.group-product-header ~ tr.child-product-row:last-of-type {
        border-left: 2px solid #a1a1a1 !important;
        border-right: 2px solid #a1a1a1 !important;
        border-bottom: 2px solid #a1a1a1 !important;
    }
    .page-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.75);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        backdrop-filter: blur(4px);
    }

    .loading-spinner-container {
        background: white;
        padding: 30px 40px;
        border-radius: 12px;
        text-align: center;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        animation: slideInUp 0.3s ease-out;
    }

    .loading-spinner {
        width: 60px;
        height: 60px;
        margin: 0 auto 20px;
        border: 4px solid #e0e0e0;
        border-top: 4px solid #289fa7;
        border-right: 4px solid #289fa7;
        border-bottom: 4px solid #289fa7;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }

    .loading-text {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        letter-spacing: 0.5px;
    }

    .loading-progress-bar {
        width: 250px;
        height: 6px;
        background-color: #e0e0e0;
        border-radius: 3px;
        overflow: hidden;
        margin-top: 10px;
    }

    .loading-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #007bff, #289fa7, #a2ffeb);
        border-radius: 3px;
        transition: width 0.2s ease;
        animation: pulse 1s infinite;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    @keyframes slideInUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes pulse {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0.7;
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/Sales/ViewSale2.vue